/**
 * Русский каталог текстов: ключ → строка с {параметрами}.
 *
 * Все тексты для игрока живут здесь; движок и приложения обращаются к ним
 * только по ключу через `t()`. Имена — интернациональные, в русской записи,
 * по 20 на пол (ключи `name.f.*` и `name.m.*`).
 */
export const RU: Record<string, string> = {
  // Служебный ключ для тестов подстановки.
  'test.greeting': 'Привет, {name}! Тебе {age} лет.',

  // Рождение.
  'birth.born': '{name} — {country}, {year} год. Жизнь началась.',
  'birth.born.neutral': 'Новая жизнь началась.',

  // Фокусы года.
  'focus.study': 'Учёба',
  'focus.play': 'Друзья и игры',
  'focus.helpFamily': 'Помощь семье',
  'focus.sport': 'Спорт',
  'focus.hobby': 'Хобби',
  'focus.work': 'Работа',
  'focus.partTime': 'Подработка',
  'focus.balance': 'Баланс',
  'focus.family': 'Семья',
  'focus.rest': 'Отдых',
  'focus.faith': 'Вера',
  'focus.career': 'Карьера',
  'focus.health': 'Здоровье',
  'focus.care': 'Забота о близком',

  // Работа: названия по уровню.
  'job.level0': 'Разнорабочий',
  'job.level1': 'Рабочий',
  'job.level2': 'Специалист',
  'job.level3': 'Руководитель',

  // Итог года: пишется движком в конце каждого прожитого года.
  'year.summary': '{ageAt} ({year} год): {focus}. Здоровье {health}, счастье {happiness}, знания {knowledge}, отношения {relations}.',

  // Экономика.
  'economy.bankruptcy': 'Долги стали непосильными — банкротство. Долг списан, но это дорого обошлось.',

  // Здоровье и болезни.
  'illness.minor': 'Болезнь выбила из колеи на несколько недель. Лечение стоило денег.',
  'illness.chronic': 'Врачи поставили хронический диагноз. Теперь это навсегда.',
  'illness.hiv': 'Анализ показал ВИЧ. С терапией можно жить долго, но риск вырос.',

  // Смерть: причина без подробностей и способа. Формулировки безличные —
  // пол жизни каталогу не известен. `{ageAt}` выводится из `age` в t().
  'death.neonatal': '{ageAt} жизнь оборвалась: первые дни после рождения оказались последними.',
  'death.infection': '{ageAt} жизнь оборвалась: инфекция, с которой организм не справился.',
  'death.maternal': '{ageAt} жизнь оборвалась: осложнения при родах.',
  'death.injury': '{ageAt} жизнь оборвалась: несчастный случай.',
  'death.traffic': '{ageAt} жизнь оборвалась: авария на дороге.',
  'death.violence': '{ageAt} жизнь оборвалась от рук другого человека.',
  'death.suicide': '{ageAt} жизнь оборвалась: добровольный уход.',
  'death.cardio': '{ageAt} жизнь оборвалась: сердце не выдержало.',
  'death.cancer': '{ageAt} жизнь оборвалась: онкология.',
  'death.respiratory': '{ageAt} жизнь оборвалась: болезнь лёгких.',
  'death.diabetes': '{ageAt} жизнь оборвалась: осложнения диабета.',
  'death.dementia': '{ageAt} жизнь оборвалась: память и силы ушли раньше, чем тело.',
  'death.other': '{ageAt} жизнь оборвалась.',
  'death.neutral': '{ageAt} жизнь оборвалась. Причину мы не называем.',

  // События года. Формулировки безличные — пол жизни каталогу не известен.
  // Тяжёлые темы (алкоголь, азарт, насилие) — без способа и сцены.
  'event.friend.new': 'Появился новый близкий друг. С ним легче.',
  'event.friend.lost': 'Старая дружба тихо закончилась. Осталась пустота.',
  'event.school.bullying': 'В школе травят. Каждое утро — через силу.',
  'event.school.award': 'Победа на олимпиаде. Учителя заметили, семья гордится.',
  'event.family.newSibling': 'В семье прибавление: родился младший.',
  'event.family.parentIll': 'Один из родителей тяжело заболел. Дом живёт по-другому.',
  'event.family.parentDied': 'Умер один из родителей. Прощание было тихим.',
  'event.family.moveCity': 'Семья переехала в город. Чужие улицы, новая школа, новые люди.',
  'event.love.first': 'Первая любовь. Мир стал громче и ярче.',
  'event.love.breakup': 'Отношения закончились. Долго не отпускало.',
  'event.work.promotion': 'Повышение. Больше ответственности — и больше денег.',
  'event.work.layoff': 'Увольнение. Работу закрыли, искать новую придётся с нуля.',
  'event.work.injury': 'Травма на работе. Несколько недель без сил и без заработка.',
  'event.money.windfall': 'Нежданные деньги: наследство от дальнего родственника.',
  'event.money.scam': 'Мошенники. Часть сбережений исчезла, вернуть не удалось.',
  'event.health.accident': 'Несчастный случай. Долгое восстановление.',
  'event.health.recovery': 'Болезнь отступила. Силы понемногу возвращаются.',
  'event.habit.smokingStart': 'Первая сигарета — за компанию. Потом вторая.',
  'event.habit.smokingQuit': 'Решение бросить курить. На этот раз получилось.',
  'event.habit.alcohol': 'Алкоголь стал привычкой. Близкие это замечают.',
  'event.habit.gambling': 'Азартные игры затянули. Деньги уходят быстрее, чем приходят.',
  'event.crime.victim': 'Нападение. Физически удалось оправиться, страх остался надолго.',
  'event.crime.domesticViolence': 'Дома небезопасно. Об этом трудно говорить даже с близкими.',
  'event.community.festival': 'Большой праздник всей общиной. Такие дни помнятся годами.',
  'event.community.disaster': 'Стихийное бедствие. Дом пострадал, восстанавливать пришлось долго.',
  'event.faith.community': 'Община стала опорой: там слушают и помогают.',
  'event.travel.first': 'Первая поездка в другую страну. Мир оказался больше, чем казалось.',
  'event.child.born': 'Родился ребёнок. Жизнь поделилась на «до» и «после».',
  'event.child.graduated': 'Ребёнок окончил учёбу. Выросла целая взрослая жизнь.',
  'event.old.grandchild': 'Родился внук. В доме снова детский смех.',
  'event.neutral': 'Этот год был трудным. Подробности мы не называем.',

  // Решения: вопрос игроку. Формулировки безличные.
  'decision.school.primary': 'Пора в школу. Идти учиться?',
  'decision.school.lowerSec': 'Начальная школа позади. Продолжать учёбу?',
  'decision.school.upperSec': 'Девять классов окончены. Что дальше?',
  'decision.school.tertiary': 'Школа окончена. Поступать в вуз?',
  'decision.work.first': 'Пора зарабатывать. Какую работу искать?',
  'decision.work.change': 'На этой работе уже семь лет. Поискать другую?',
  'decision.move.city': 'В селе перспектив мало. Переехать в город?',
  'decision.move.abroad': 'Есть шанс уехать в другую страну. Попробовать?',
  'decision.love.partner': 'Одиночество надоело. Искать близкого человека?',
  'decision.family.child': 'Семья думает о ребёнке. Заводить?',
  'decision.health.treatment': 'Нужно лечение, и оно стоит денег. Лечиться сейчас?',
  'decision.money.invest': 'Накопились сбережения. Вложить их?',
  'decision.work.retire': 'Возраст пенсионный. Уйти на покой?',
  'decision.family.careParents': 'Родителям нужна помощь. Взять заботу на себя?',

  // Решения: варианты.
  'option.school.primary.study': 'Учиться',
  'option.school.primary.skip': 'Помогать семье',
  'option.school.lowerSec.study': 'Продолжить учёбу',
  'option.school.lowerSec.skip': 'Бросить школу',
  'option.school.upperSec.school': 'Старшие классы',
  'option.school.upperSec.vocational': 'Училище',
  'option.school.upperSec.work': 'Работать',
  'option.school.tertiary.university': 'Университет',
  'option.school.tertiary.work': 'Работать',
  'option.work.first.level0': 'Разнорабочим',
  'option.work.first.level1': 'Рабочим',
  'option.work.first.level2': 'Специалистом',
  'option.work.first.level3': 'Руководителем',
  'option.work.change.change': 'Сменить работу',
  'option.work.change.stay': 'Остаться',
  'option.move.city.move': 'Переехать',
  'option.move.city.stay': 'Остаться',
  'option.move.abroad.go': 'Уехать',
  'option.move.abroad.stay': 'Остаться дома',
  'option.love.partner.seek': 'Искать',
  'option.love.partner.alone': 'Пока одному',
  'option.family.child.have': 'Да',
  'option.family.child.wait': 'Подождать',
  'option.health.treatment.treat': 'Лечиться',
  'option.health.treatment.wait': 'Подождать',
  'option.money.invest.invest': 'Вложить',
  'option.money.invest.save': 'Сберечь',
  'option.work.retire.retire': 'На пенсию',
  'option.work.retire.continue': 'Работать дальше',
  'option.family.careParents.care': 'Ухаживать',
  'option.family.careParents.no': 'Не получится',

  // Решения: почему вариант недоступен.
  'reason.noSchoolNearby': 'Рядом нет школы',
  'reason.lowKnowledge': 'Не хватает знаний',
  'reason.noMoney': 'Не хватает денег',
  'reason.noQualification': 'Не хватает квалификации',
  'reason.unavailable': 'Сейчас недоступно',

  // Пояснения к вариантам решений (A4): вероятные последствия словами, без чисел.
  'hint.school.primary.study': 'скорее всего: знания ↑, работа позже, но лучше',
  'hint.school.primary.skip': 'скорее всего: семье легче сейчас, знания и доход ниже на всю жизнь',
  'hint.school.lowerSec.study': 'скорее всего: знания ↑, шанс на хорошую работу ↑',
  'hint.school.lowerSec.skip': 'скорее всего: ранний заработок, знания и доход ниже',
  'hint.school.upperSec.school': 'скорее всего: знания ↑, дорога в вуз открыта, денег пока нет',
  'hint.school.upperSec.vocational': 'скорее всего: ремесло в руках, работа раньше, потолок дохода ниже',
  'hint.school.upperSec.work': 'скорее всего: деньги сейчас, знания и доход дальше ниже',
  'hint.school.tertiary.university': 'скорее всего: знания и счастье ↑, доход выше потом; несколько лет без заработка',
  'hint.school.tertiary.work': 'скорее всего: доход сразу, отношения ↑, потолок карьеры ниже',
  'hint.work.first.level0': 'скорее всего: небольшой доход, тяжёлый труд, здоровье ↓',
  'hint.work.first.level1': 'скорее всего: стабильный доход, рост медленный',
  'hint.work.first.level2': 'скорее всего: доход выше среднего, есть куда расти',
  'hint.work.first.level3': 'скорее всего: высокий доход, больше ответственности',
  'hint.work.change.change': 'скорее всего: доход ↑, но можно на время остаться без работы',
  'hint.work.change.stay': 'скорее всего: всё как прежде, спокойно и без роста',
  'hint.move.city.move': 'скорее всего: доход и знания ↑, связь с роднёй ↓',
  'hint.move.city.stay': 'скорее всего: отношения ↑, доход ниже',
  'hint.move.abroad.go': 'скорее всего: шанс на доход ↑, но может не выйти — тогда деньги и счастье ↓',
  'hint.move.abroad.stay': 'скорее всего: отношения ↑, остальное как прежде',
  'hint.love.partner.seek': 'скорее всего: отношения и счастье ↑, не сразу',
  'hint.love.partner.alone': 'скорее всего: ничего не меняется; одиночество позже отнимает счастье',
  'hint.family.child.have': 'скорее всего: счастье и отношения ↑, расходы ↑ на много лет',
  'hint.family.child.wait': 'скорее всего: ничего не меняется',
  'hint.health.treatment.treat': 'скорее всего: здоровье ↑, деньги ↓ или долг',
  'hint.health.treatment.wait': 'скорее всего: деньги целы, здоровье ↓',
  'hint.money.invest.invest': 'скорее всего: сбережения растут быстрее, но могут и упасть',
  'hint.money.invest.save': 'скорее всего: деньги целы, растут медленно',
  'hint.work.retire.retire': 'скорее всего: доход ↓ до пенсии, здоровье и счастье ↑',
  'hint.work.retire.continue': 'скорее всего: доход сохраняется, здоровье ↓',
  'hint.family.careParents.care': 'скорее всего: отношения ↑, деньги и силы ↓',
  'hint.family.careParents.no': 'скорее всего: деньги целы, отношения и счастье ↓',

  // Решения: записи журнала.
  'decision.school.primary.study': 'Первый класс. Тетради, доска, новые лица.',
  'decision.school.primary.skip': 'Школа не случилась: дома нужны руки.',
  'decision.school.lowerSec.study': 'Учёба продолжается: средние классы.',
  'decision.school.lowerSec.skip': 'Школа позади раньше срока.',
  'decision.school.upperSec.school': 'Старшие классы. Впереди экзамены.',
  'decision.school.upperSec.vocational': 'Училище: ремесло в руках — хлеб на столе.',
  'decision.school.upperSec.work': 'Вместо парты — первая смена.',
  'decision.school.tertiary.university': 'Поступление. Студенческие годы начались.',
  'decision.school.tertiary.work': 'Диплом подождёт: нужно зарабатывать.',
  'decision.work.first.hired': 'Первая работа: {job}. Первые свои деньги.',
  'decision.work.first.rehired': 'Новая работа: {job}.',
  'decision.work.change.change': 'Новое место: {job}. Всё с чистого листа.',
  'decision.work.change.stay': 'Работа та же. Стабильность дороже.',
  'decision.move.city.move': 'Переезд в город. Чужие улицы, новая жизнь.',
  'decision.move.city.stay': 'Остались дома: здесь корни.',
  'decision.move.abroad.go.success': 'Переезд в другую страну: теперь это {country}. Всё заново — язык, работа, друзья.',
  'decision.move.abroad.go.fail': 'Уехать не вышло: отказ в визе, деньги на попытку потрачены зря.',
  'decision.move.abroad.stay': 'Решение остаться. Дом есть дом.',
  'decision.love.partner.seek.found': 'Встреча, которая всё изменила. Теперь вдвоём.',
  'decision.love.partner.seek.alone': 'Отношения не сложились. Пока одному.',
  'decision.love.partner.alone': 'Сейчас не до отношений.',
  'decision.family.child.have': 'В семье прибавление: родился ребёнок.',
  'decision.family.child.wait': 'С детьми решили подождать.',
  'decision.health.treatment.treat': 'Лечение помогло, хотя счёт был заметный.',
  'decision.health.treatment.wait': 'Лечение отложено. Здоровье это запомнило.',
  'decision.money.invest.invest.gain': 'Вложение окупилось: сбережения выросли.',
  'decision.money.invest.invest.loss': 'Вложение прогорело: часть сбережений потеряна.',
  'decision.money.invest.save': 'Деньги остались под подушкой. Спокойнее.',
  'decision.work.retire.retire': 'Пенсия. Впервые за много лет утро без будильника.',
  'decision.work.retire.continue': 'Пенсия подождёт: работа пока по силам.',
  'decision.family.careParents.care': 'Забота о родителях: время и деньги, зато рядом.',
  'decision.family.careParents.no': 'Ухаживать за родителями не вышло. Осадок остался.',

  // Образование (для отчёта и карточки жизни).
  'education.none': 'Без образования',
  'education.primary': 'Начальное',
  'education.lowerSec': 'Основное среднее',
  'education.upperSec': 'Полное среднее',
  'education.vocational': 'Среднее профессиональное',
  'education.university': 'Высшее',

  // Причины смерти: короткие названия для отчёта.
  'cause.neonatal': 'Неонатальные причины',
  'cause.infection': 'Инфекции',
  'cause.maternal': 'Осложнения при родах',
  'cause.injury': 'Травмы',
  'cause.traffic': 'ДТП',
  'cause.violence': 'Насилие',
  'cause.suicide': 'Суицид',
  'cause.cardio': 'Сердце и сосуды',
  'cause.cancer': 'Онкология',
  'cause.respiratory': 'Болезни лёгких',
  'cause.diabetes': 'Диабет',
  'cause.dementia': 'Деменция',
  'cause.other': 'Прочее',

  // Пол.
  'sex.f': 'женщины',
  'sex.m': 'мужчины',
  'sex.f.person': 'женщина',
  'sex.m.person': 'мужчина',

  // Калибровочный отчёт консольного прогона.
  'report.title': 'Калибровочный отчёт: {n} жизней',
  'report.lifespan.title': 'Продолжительность жизни, лет (модель / ожидаемая e0 по странам рождения)',
  'report.lifespan.row': '  {sex}: {lived} / {e0} (разница {diff})',
  'report.countries.title': 'Топ-5 стран рождения (доля в прогоне / доля по рождениям)',
  'report.countries.row': '  {name} ({iso3}): {share} % / {expected} %',
  'report.causes.title': 'Причины смерти по таблице игры (не сверка с ВОЗ)',
  'report.education.title': 'Образование',
  'report.share.row': '  {name}: {share} %',
  'report.bankrupt': 'Банкротства: {share} % жизней',
  'report.emigration': 'Эмиграция: {share} % жизней',
  'report.gambling': 'Игровая зависимость (состояние gambling): {share} % жизней',
  'report.none': '  (нет данных)',

  // Карточка одной жизни.
  'life.card': '{name}, {sex} — {country}, {year} год рождения. Seed: {seed}.',
  'life.card.education': 'Образование: {education}.',
  'life.card.alive': 'Жизнь продолжается, возраст {age}.',
  'life.card.dead': 'Прожито лет: {age}. Причина: {cause}.',
  'life.log.line': '[{age}] {text}',

  // Консоль.
  'cli.badValue': 'Недопустимое значение {name}: {value}. Допустимо: {allowed}.',
  'cli.policy.note': 'Политика: {policy}.',
  'cli.policy.autopilot': 'автопилот (в азарт не играет)',
  'cli.policy.gamble': 'играет раз в год при деньгах, ставка {share} % сбережений',

  // Куда обратиться: всегда после тяжёлых эпизодов.
  'help.lines': 'Если тяжело — позвони: телефон доверия 8-800-2000-122 (бесплатно, для детей, подростков и родителей), экстренная помощь 112.',
  'help.gambling': 'Игра стала тянуть сильнее, чем хотелось бы. Это лечится: бесплатная помощь при игровой зависимости — телефон доверия 8-800-2000-122, анонимные группы «Игроки анонимные».',

  // Тяга к игре (мини-игры, раздел E).
  'urge.offer': 'Снова тянет сыграть «ещё разок». Год прошёл в мыслях о ставках.',

  // Мини-игры, раздел E (казино): ожидание на столе и записи журнала.
  'minigame.expect.lottery': 'В среднем теряете {loss} % цены билета.',
  'minigame.expect.roulette': 'В среднем теряете {loss} % ставки за круг.',
  'minigame.expect.slots': 'В среднем теряете {loss} % ставки за прокрут.',
  'minigame.expect.blackjack': 'В среднем теряете {loss} % ставки за руку.',
  'minigame.expect.dice': 'В среднем теряете {loss} % ставки за бросок.',
  'minigame.expect.bets': 'В среднем теряете {loss} % ставки.',
  'minigame.lottery.lose': 'Лотерейный билет не выиграл: −{amount}.',
  'minigame.lottery.refund': 'Лотерейный билет окупился: вернули цену билета.',
  'minigame.lottery.win': 'Лотерейный билет принёс выигрыш ×{mult}: +{amount}.',
  'minigame.lottery.jackpot': 'Джекпот в лотерее: +{amount}. Один шанс из четырнадцати миллионов.',
  'minigame.roulette.win': 'Рулетка: выпало {pocket}, выигрыш +{amount}.',
  'minigame.roulette.lose': 'Рулетка: выпало {pocket}, ставка −{amount} ушла заведению.',
  'minigame.slots.win': 'Слоты: {spins} прокрут(а/ов), итог +{amount}.',
  'minigame.slots.lose': 'Слоты: {spins} прокрут(а/ов), итог −{amount}.',
  'minigame.blackjack.natural': 'Блэкджек с двух карт ({player} против {dealer}): +{amount}.',
  'minigame.blackjack.win': 'Блэкджек: {player} против {dealer} у дилера, +{amount}.',
  'minigame.blackjack.push': 'Блэкджек: ничья, {player} против {dealer}. Ставка вернулась.',
  'minigame.blackjack.lose': 'Блэкджек: {player} против {dealer} у дилера, −{amount}.',
  'minigame.dice.win': 'Кости: выпало ровно 7 ({a}+{b}), выигрыш +{amount}.',
  'minigame.dice.push': 'Кости: выпало {sum} ({a}+{b}), ставка вернулась.',
  'minigame.dice.lose': 'Кости: выпало {sum} ({a}+{b}), −{amount}.',
  'minigame.dice.robbed': 'После игры в кости ограбили: забрали всё, что было с собой (−{amount}), и избили.',
  'minigame.dice.arrest': 'Облава на уличных игроков: штраф и ночь в участке, −{amount}.',
  'minigame.bets.placed': 'Ставка {amount} на «{team}» с коэффициентом {odds}. Итог — в конце года.',
  'minigame.bets.won': 'Ставка сыграла: +{amount}.',
  'minigame.bets.lost': 'Ставка не сыграла: −{amount} ушли букмекеру.',
  'minigame.bets.team.1': 'Северный Маяк',
  'minigame.bets.team.2': 'Речной Порт',
  'minigame.bets.team.3': 'Горный Ветер',
  'minigame.bets.team.4': 'Старый Город',
  'minigame.bets.team.5': 'Заводская',
  'minigame.bets.team.6': 'Южная Звезда',
  'minigame.bets.team.7': 'Университет',
  'minigame.bets.team.8': 'Железная Дорога',

  // Мини-игры, раздел E (деньги, E8–E10): ожидание и записи журнала.
  'minigame.expect.pyramid': 'Обещают {yield} % в год. На третий год с вероятностью {crash} % всё вложенное пропадает.',
  'minigame.expect.crypto': 'Ожидание +{gain} % в год при разбросе {sigma} %. Курс виден только по тапу, и каждый взгляд тянет сыграть (+{urge}).',
  'minigame.expect.stocks': 'Это не азарт: ожидание +{gain} % в год при разбросе {sigma} %, тяги нет. Забрать можно в любой год.',
  'minigame.expect.loans': 'Ломбард: через год вернуть на {pawn} % больше. Микрозайм: через год вернуть на {micro} % больше. Не вернёте — долг и коллекторы.',
  'minigame.pyramid.invest': 'Вложили {amount} в «проект знакомого под 40 % годовых».',
  'minigame.pyramid.decline': 'Знакомый предлагал «вложение под 40 % в год». Отказ.',
  'minigame.crypto.buy': 'Купили монету на {amount}.',
  'minigame.crypto.sell': 'Продали монету: +{amount}.',
  'minigame.stocks.buy': 'Купили акции на {amount}.',
  'minigame.stocks.sell': 'Продали акции: +{amount}.',
  'minigame.loans.pawn': 'Заложили вещи в ломбард: +{amount}, через год вернуть {due}.',
  'minigame.loans.microloan': 'Взяли микрозайм: +{amount}, через год вернуть {due}.',
  'pyramid.payout': 'Пирамида выплатила обещанные проценты: +{amount}.',
  'pyramid.crash': 'Пирамида рухнула. Вложенные {amount} не вернулись, «знакомый» не отвечает.',
  'pyramid.exit': 'Успели выйти из пирамиды до краха: +{amount}. Повезло, как одному из семи.',
  'loan.repaid': 'Вернули заём: −{amount}.',
  'loan.default': 'Заём не вернули: {amount} ушли в долг.',
  'loan.collectors': 'Приходили коллекторы. Звонки родным, угрозы, бессонница.',

  // Мини-игры, раздел F (умение, F1–F7): записи журнала.
  'minigame.exam.pass': 'Экзамен сдан: {correct} из {total} верных. Поступили.',
  'minigame.exam.fail': 'Экзамен не сдан: {correct} из {total} верных. Дальше — как сложится.',
  'minigame.interview.up': 'Собеседование прошло лучше, чем ждали: место уровня {level}.',
  'minigame.interview.same': 'Собеседование прошло ровно: место уровня {level}.',
  'minigame.interview.down': 'Собеседование не задалось: место уровня {level}.',
  'minigame.interview.stay': 'Предложили не лучше нынешнего: остались на месте уровня {level}.',
  'minigame.bargain.deal': 'Сторговались с {tries}-й попытки: сэкономили {amount}.',
  'minigame.bargain.none': 'Продавец не уступил. Купили за {amount}, как просили.',
  'minigame.bargain.lost': 'Предложили слишком мало — продавец ушёл. Взяли у другого дороже: −{amount}.',
  'minigame.field.harvest': 'Осень на поле: урожай принёс {amount} против посева.',
  'minigame.field.drought': 'Засуха. Урожай против посева: {amount}.',
  'minigame.field.crop.0': 'Просо: урожай ровный, без сюрпризов',
  'minigame.field.crop.1': 'Кукуруза: бывает и больше, и меньше',
  'minigame.field.crop.2': 'Хлопок на продажу: или густо, или пусто',
  'minigame.driving.pass': 'Сдали на права с {correct} из {total}: −{amount} за экзамен. Теперь за рулём, и риск аварии выше.',
  'minigame.driving.fail': 'Экзамен на права провалили ({correct} из {total}): −{amount}. Второй попытки не было.',
  'minigame.driving.go': 'Ехать',
  'minigame.driving.wait': 'Ждать',
  'minigame.driving.s.0': 'Жёлтый мигает, у перехода стоит пешеход.',
  'minigame.driving.s.1': 'Зелёный, перекрёсток пуст.',
  'minigame.driving.s.2': 'Школьный автобус остановился с мигающими огнями.',
  'minigame.driving.s.3': 'Вы на главной, справа никого.',
  'minigame.driving.s.4': 'Знак «Стоп», из-за угла выезжает грузовик.',
  'minigame.driving.s.5': 'Регулировщик машет вам проезжать.',
  'minigame.driving.s.6': 'Туман, впереди горят стоп-сигналы.',
  'minigame.driving.s.7': 'Стрелка направо горит, тротуар пуст.',
  'minigame.busking.crowd.small': 'Играли на улице. Прохожие шли мимо, но в футляр набралось +{amount}. Это было в радость.',
  'minigame.busking.crowd.mid': 'Играли на улице. Несколько человек остановились послушать: +{amount}.',
  'minigame.busking.crowd.big': 'Играли на улице. Собралась толпа, хлопали: +{amount}.',
  'minigame.cooking.ing.0': 'Рис',
  'minigame.cooking.ing.1': 'Баранина',
  'minigame.cooking.ing.2': 'Фасоль',
  'minigame.cooking.ing.3': 'Рыба',
  'minigame.cooking.ing.4': 'Тыква',
  'minigame.cooking.ing.5': 'Травы и специи',
  'minigame.cooking.dish.0': 'На праздник готовили с рисом. Хватило всем, и соседи просили добавки.',
  'minigame.cooking.dish.1': 'На праздник готовили баранину. Весь двор собрался на запах.',
  'minigame.cooking.dish.2': 'На праздник готовили фасоль. Просто, сытно, по-домашнему.',
  'minigame.cooking.dish.3': 'На праздник готовили рыбу. Спорили, как правильно, и смеялись.',
  'minigame.cooking.dish.4': 'На праздник готовили тыкву. Дети таскали куски прямо со стола.',
  'minigame.cooking.dish.5': 'На праздник главным были травы и специи. Все спрашивали рецепт.',

  // G1. Двор: друзья после трёх «позвать играть».
  'minigame.yard.friends.0': 'Звали соседских детей играть. В этот раз никто не вышел — бывает.',
  'minigame.yard.friends.1': 'Во дворе появился друг. Играли до темноты.',
  'minigame.yard.friends.2': 'Во дворе появились два друга. Теперь есть с кем играть.',
  'minigame.yard.friends.3': 'Весь двор вышел играть. Друзей стало трое.',

  // G2. Урок: предметы и итог по месту «сильного» предмета.
  'minigame.lesson.subject.math': 'Математика',
  'minigame.lesson.subject.lang': 'Язык и чтение',
  'minigame.lesson.subject.sport': 'Физкультура',
  'minigame.lesson.subject.art': 'Рисование и музыка',
  'minigame.lesson.strong': 'Говорят, в этой школе сильный учитель по предмету «{subject}».',
  'minigame.lesson.result.3': 'Любимый предмет совпал с сильным учителем: учёба пошла легко.',
  'minigame.lesson.result.2': 'Школьные годы: что-то интересно, что-то нет. Знания растут понемногу.',
  'minigame.lesson.result.1': 'Самое интересное оказалось не тем, чему здесь хорошо учат. Но что-то осталось.',

  // G3. Буллинг: реплики без сцен, три ответа и блок «куда обратиться».
  'minigame.bullying.line.0': 'В классе начали цепляться. Что делаете?',
  'minigame.bullying.line.0.silent': 'Промолчать',
  'minigame.bullying.line.0.reply': 'Ответить',
  'minigame.bullying.line.0.adult': 'Рассказать взрослому',
  'minigame.bullying.line.1': 'Стало хуже: забрали вещи. Что делаете?',
  'minigame.bullying.line.1.silent': 'Промолчать',
  'minigame.bullying.line.1.reply': 'Потребовать вернуть',
  'minigame.bullying.line.1.adult': 'Сказать учителю',
  'minigame.bullying.line.2': 'Дома спрашивают, почему такой тихий. Что делаете?',
  'minigame.bullying.line.2.silent': 'Сказать, что всё нормально',
  'minigame.bullying.line.2.reply': 'Сказать, что сами разберётесь',
  'minigame.bullying.line.2.adult': 'Рассказать как есть',
  'minigame.bullying.stopped': 'Травля в школе прекратилась. Рядом оказались те, кто помог.',
  'minigame.bullying.continued': 'Травля в школе не прекратилась в этом году. Это не ваша вина.',
  'minigame.bullying.help': 'Если травят вас или кого-то рядом: расскажите взрослому, которому доверяете, — родителю, учителю, школьному психологу. Это не слабость, а правильный шаг.',

  // G4. Первая любовь: три части сообщения по четыре варианта.
  'minigame.firstLove.greeting.0': 'Привет!',
  'minigame.firstLove.greeting.1': 'Здравствуй.',
  'minigame.firstLove.greeting.2': 'Эй, это я.',
  'minigame.firstLove.greeting.3': 'Доброе утро.',
  'minigame.firstLove.core.0': 'Мне нравится с тобой говорить.',
  'minigame.firstLove.core.1': 'Пойдёшь со мной гулять в субботу?',
  'minigame.firstLove.core.2': 'Я думаю о тебе весь день.',
  'minigame.firstLove.core.3': 'Хочу показать тебе одно место.',
  'minigame.firstLove.signature.0': 'Жду ответа.',
  'minigame.firstLove.signature.1': 'Не обязательно отвечать сразу.',
  'minigame.firstLove.signature.2': 'Твой друг.',
  'minigame.firstLove.signature.3': 'Обнимаю.',
  'minigame.firstLove.match.0': 'Сообщение получилось не в тон, но ответ всё равно пришёл. Первая любовь.',
  'minigame.firstLove.match.1': 'Сообщение прочитали и улыбнулись. Первая любовь.',
  'minigame.firstLove.match.2': 'Ответили почти сразу. Первая любовь — и, кажется, взаимная.',
  'minigame.firstLove.match.3': 'Как будто вы знали друг друга всегда. Первая любовь.',

  // G5. Свадьба: расходы и итог.
  'minigame.wedding.item.guests': 'Гости',
  'minigame.wedding.item.food': 'Угощение',
  'minigame.wedding.item.dress': 'Наряд',
  'minigame.wedding.item.music': 'Музыка',
  'minigame.wedding.item.photo': 'Фотограф',
  'minigame.wedding.item.none': 'Без гостей, только вдвоём',
  'minigame.wedding.done': 'Свадьба. Потратили {amount}, и ни о чём не жалели.',
  'minigame.wedding.helped': 'Свадьба. Родители помогли на {help}; из своих ушло {amount}.',

  // G6. Ночь: укачать ребёнка.
  'minigame.night.slept': 'Ночь с малышом прошла спокойно: попали в ритм, выспались.',
  'minigame.night.tired': 'Ночь с малышом: промахов — {misses}. Недосып, зато ребёнок уснул.',

  // G7. Разговор с ребёнком: проблема и три ответа (услышит / нейтральный / холодный).
  'minigame.talk.school': 'Ребёнок пришёл расстроенный: в школе не ладится.',
  'minigame.talk.school.0': 'Расскажи, что случилось. Я слушаю.',
  'minigame.talk.school.1': 'Бывает. Завтра будет лучше.',
  'minigame.talk.school.2': 'Надо было стараться.',
  'minigame.talk.friends': 'Ребёнок поссорился с друзьями и молчит весь вечер.',
  'minigame.talk.friends.0': 'Хочешь, посидим вместе? Расскажешь, когда будешь готов.',
  'minigame.talk.friends.1': 'Помиритесь, это проходит.',
  'minigame.talk.friends.2': 'Не выдумывай, у тебя всё есть.',
  'minigame.talk.money': 'Ребёнок просит денег на то, что есть у всех в классе.',
  'minigame.talk.money.0': 'Давай посчитаем вместе, что мы можем.',
  'minigame.talk.money.1': 'Посмотрим в следующем месяце.',
  'minigame.talk.money.2': 'Денег нет, и хватит об этом.',
  'minigame.talk.heard': 'Разговор с ребёнком: он почувствовал, что его услышали.',
  'minigame.talk.neutral': 'Разговор с ребёнком получился обычным. Ни хуже, ни лучше.',
  'minigame.talk.cold': 'Разговор с ребёнком вышел холодным. Он закрылся.',

  // G8. Очередь: пять окошек поликлиники.
  'minigame.clinic.window.private': 'Частный врач',
  'minigame.clinic.window.paid': 'Платная очередь',
  'minigame.clinic.window.cheap': 'Недорогое окно',
  'minigame.clinic.window.free': 'Бесплатное окно',
  'minigame.clinic.window.none': 'Уйти без лечения',
  'minigame.clinic.treated': 'Очередь к врачу: дождались приёма через {days} дн.',
  'minigame.clinic.closed': 'Очередь к врачу: простояли зря — врача нет.',
  'minigame.clinic.left': 'Очередь к врачу: не стали ждать и ушли.',

  // G9. Чемодан: восемь вещей, взять четыре; эффект написан на вещи.
  'minigame.suitcase.item.documents': 'Документы',
  'minigame.suitcase.item.money': 'Деньги',
  'minigame.suitcase.item.photo': 'Фотографии',
  'minigame.suitcase.item.book': 'Книга',
  'minigame.suitcase.item.instrument': 'Инструмент',
  'minigame.suitcase.item.gift': 'Подарок',
  'minigame.suitcase.item.food': 'Еда в дорогу',
  'minigame.suitcase.item.phone': 'Телефон',
  'minigame.suitcase.effect.documents.abroad': 'Без них шанс уехать −20 %',
  'minigame.suitcase.effect.documents.city': 'Без них счастье −2: беготня по справкам',
  'minigame.suitcase.effect.instrument': 'С шансом 30 % первая работа уровнем выше',
  'minigame.suitcase.effect.photo': 'Счастье +2 в первый год',
  'minigame.suitcase.packed': 'Чемодан собран: документы на месте.',
  'minigame.suitcase.noDocuments': 'Чемодан собран, а документы остались дома.',

  // G10. Граница: три вопроса офицера, три ответа; верный — первый в банке.
  'minigame.border.purpose': 'Цель поездки?',
  'minigame.border.purpose.0': 'Работа и учёба, вот приглашение.',
  'minigame.border.purpose.1': 'Посмотреть, как получится.',
  'minigame.border.purpose.2': 'А вам зачем?',
  'minigame.border.means': 'На что будете жить?',
  'minigame.border.means.0': 'Есть сбережения, вот выписка.',
  'minigame.border.means.1': 'Найду что-нибудь на месте.',
  'minigame.border.means.2': 'Друзья помогут.',
  'minigame.border.return': 'Когда вернётесь?',
  'minigame.border.return.0': 'По окончании контракта, дата в документах.',
  'minigame.border.return.1': 'Пока не планирую.',
  'minigame.border.return.2': 'Как пойдёт.',
  'minigame.border.calm': 'Граница: ответы были спокойными и точными.',
  'minigame.border.ok': 'Граница: вопросы задали, ответы приняли.',
  'minigame.border.tense': 'Граница: разговор вышел напряжённым, офицер хмурился.',

  // G11. Смена: игра по типу работы.
  'minigame.shift.kind.field': 'Собрать урожай',
  'minigame.shift.kind.factory': 'Конвейер',
  'minigame.shift.kind.office': 'Сортировка',
  'minigame.shift.kind.trade': 'Сдача',
  'minigame.shift.card.invoice': 'Счёт',
  'minigame.shift.card.receipt': 'Чек',
  'minigame.shift.card.payroll': 'Ведомость',
  'minigame.shift.card.letter': 'Письмо',
  'minigame.shift.card.memo': 'Записка',
  'minigame.shift.card.request': 'Заявка',
  'minigame.shift.stack.money': 'Деньги',
  'minigame.shift.stack.papers': 'Бумаги',
  'minigame.shift.sharp': 'Смена прошла чётко: точность {accuracy} %. Начальство заметило.',
  'minigame.shift.done': 'Обычная смена: точность {accuracy} %.',
  'minigame.shift.tired': 'Смена не задалась: точность {accuracy} %. Устали сильнее обычного.',

  // G12. Собрать месяц: шесть статей, вычеркнуть две.
  'minigame.budget.item.food': 'Еда',
  'minigame.budget.item.rent': 'Жильё',
  'minigame.budget.item.treatment': 'Лечение',
  'minigame.budget.item.school': 'Школа детям',
  'minigame.budget.item.study': 'Свои курсы',
  'minigame.budget.item.transport': 'Транспорт',
  'minigame.budget.item.leisure': 'Отдых',
  'minigame.budget.effect.food': 'Здоровье −5',
  'minigame.budget.effect.rent': 'Съехать к родным: отношения −2',
  'minigame.budget.effect.treatment': 'Здоровье −3',
  'minigame.budget.effect.school': 'Дети это запомнят',
  'minigame.budget.effect.study': 'Знания −2',
  'minigame.budget.effect.transport': 'Счастье −1',
  'minigame.budget.effect.leisure': 'Счастье −2',
  'minigame.budget.done': 'Месяц собрали: ужались, но дотянули.',
  'minigame.budget.hungry': 'Месяц собрали, урезав еду. Тело это заметило.',
  'minigame.budget.untreated': 'Месяц собрали, отложив лечение.',

  // G13. Отказ: удержать «Не сегодня».
  'minigame.refusal.hold': 'Не сегодня',
  'minigame.refusal.habit.smoking': 'Хочется закурить.',
  'minigame.refusal.habit.alcohol': 'Хочется выпить.',
  'minigame.refusal.habit.gambling': 'Хочется сыграть.',
  'minigame.refusal.habit.urge': 'Тянет сыграть ещё.',
  'minigame.refusal.help': 'Если тяга сильнее вас — это лечится. Телефон доверия 8-800-2000-122, анонимные группы, участковый врач.',
  'minigame.refusal.held': 'Сегодня удержались. Один день из многих.',
  'minigame.refusal.slipped': 'Сегодня не удержались. Завтра — снова.',

  // G14. Что оставить: шесть наследств по получателям.
  'minigame.legacy.item.money': 'Деньги',
  'minigame.legacy.item.house': 'Дом',
  'minigame.legacy.item.letter': 'Письмо',
  'minigame.legacy.item.thing': 'Вещь',
  'minigame.legacy.item.nothing': 'Ничего',
  'minigame.legacy.item.debt': 'Долг',
  'minigame.legacy.to.children': 'Детям',
  'minigame.legacy.to.cohort': 'Когорте',
  'minigame.legacy.done': 'На пенсию. Решили, что кому останется.',

  // G15. Память: пары воспоминаний.
  'minigame.memory.filler.0': 'Первый друг',
  'minigame.memory.filler.1': 'Запах дома',
  'minigame.memory.filler.2': 'Дорога куда-то',
  'minigame.memory.found': 'Перебирали воспоминания: все сошлись. Тепло.',
  'minigame.memory.faded': 'Перебирали воспоминания: сошлись {found} из трёх. Остальные ещё придут.',
  'minigame.memory.quiet': 'Перебирали старые фотографии. Вспомнилось не всё, но тепло.',

  // F2. Собеседование: три яруса × три реплики × три ответа (честный / уверенный / скромный).
  'minigame.interview.0.0': 'Работа простая, но тяжёлая. Справитесь?',
  'minigame.interview.0.0.honest': 'Справлюсь, если покажете, как надо.',
  'minigame.interview.0.0.confident': 'Я справлюсь с чем угодно.',
  'minigame.interview.0.0.modest': 'Постараюсь. Научусь по ходу.',
  'minigame.interview.0.1': 'Опыт есть?',
  'minigame.interview.0.1.honest': 'Немного, в основном дома и у родни.',
  'minigame.interview.0.1.confident': 'Большой. Всё умею.',
  'minigame.interview.0.1.modest': 'Почти нет, но я быстро учусь.',
  'minigame.interview.0.2': 'Почему к нам?',
  'minigame.interview.0.2.honest': 'Рядом с домом, и работа нужна.',
  'minigame.interview.0.2.confident': 'Хочу расти в вашей компании.',
  'minigame.interview.0.2.modest': 'Возьмёте — буду благодарен.',
  'minigame.interview.1.0': 'Расскажите, что умеете.',
  'minigame.interview.1.0.honest': 'Вот что делал и где. Чего не умею — скажу прямо.',
  'minigame.interview.1.0.confident': 'Всё, что потребуется.',
  'minigame.interview.1.0.modest': 'Да так, ничего особенного.',
  'minigame.interview.1.1': 'Что будете делать, если ошибётесь?',
  'minigame.interview.1.1.honest': 'Скажу сразу и исправлю.',
  'minigame.interview.1.1.confident': 'Я не ошибаюсь.',
  'minigame.interview.1.1.modest': 'Надеюсь, такого не случится.',
  'minigame.interview.1.2': 'Какую оплату ждёте?',
  'minigame.interview.1.2.honest': 'Как у других на этом месте.',
  'minigame.interview.1.2.confident': 'Выше рынка, я того стою.',
  'minigame.interview.1.2.modest': 'Сколько дадите.',
  'minigame.interview.2.0': 'Почему мы должны выбрать вас?',
  'minigame.interview.2.0.honest': 'У меня есть нужный опыт.',
  'minigame.interview.2.0.confident': 'Потому что я сделаю это лучше остальных.',
  'minigame.interview.2.0.modest': 'Не знаю, есть, наверное, сильнее.',
  'minigame.interview.2.1': 'Что вы сделаете в первые три месяца?',
  'minigame.interview.2.1.honest': 'Разберусь, как всё устроено, потом предложу план.',
  'minigame.interview.2.1.confident': 'Возьму ответственность и покажу результат.',
  'minigame.interview.2.1.modest': 'Буду делать, что скажут.',
  'minigame.interview.2.2': 'Вы спорите с руководством?',
  'minigame.interview.2.2.honest': 'Если вижу ошибку — говорю.',
  'minigame.interview.2.2.confident': 'Да, когда уверен, и отстаиваю.',
  'minigame.interview.2.2.modest': 'Стараюсь не спорить.',

  // F1. Экзамен: банк из 60 вопросов по трём темам (столицы, арифметика, «что дороже»), по 4 варианта.
  'minigame.exam.geo.0': 'Столица Франции?',
  'minigame.exam.geo.0.0': 'Лион',
  'minigame.exam.geo.0.1': 'Париж',
  'minigame.exam.geo.0.2': 'Марсель',
  'minigame.exam.geo.0.3': 'Бордо',
  'minigame.exam.geo.1': 'Столица Японии?',
  'minigame.exam.geo.1.0': 'Токио',
  'minigame.exam.geo.1.1': 'Осака',
  'minigame.exam.geo.1.2': 'Киото',
  'minigame.exam.geo.1.3': 'Нагоя',
  'minigame.exam.geo.2': 'Столица Бразилии?',
  'minigame.exam.geo.2.0': 'Рио-де-Жанейро',
  'minigame.exam.geo.2.1': 'Сан-Паулу',
  'minigame.exam.geo.2.2': 'Бразилиа',
  'minigame.exam.geo.2.3': 'Салвадор',
  'minigame.exam.geo.3': 'Столица Австралии?',
  'minigame.exam.geo.3.0': 'Сидней',
  'minigame.exam.geo.3.1': 'Мельбурн',
  'minigame.exam.geo.3.2': 'Перт',
  'minigame.exam.geo.3.3': 'Канберра',
  'minigame.exam.geo.4': 'Столица Канады?',
  'minigame.exam.geo.4.0': 'Торонто',
  'minigame.exam.geo.4.1': 'Оттава',
  'minigame.exam.geo.4.2': 'Ванкувер',
  'minigame.exam.geo.4.3': 'Монреаль',
  'minigame.exam.geo.5': 'Столица Турции?',
  'minigame.exam.geo.5.0': 'Стамбул',
  'minigame.exam.geo.5.1': 'Анкара',
  'minigame.exam.geo.5.2': 'Измир',
  'minigame.exam.geo.5.3': 'Анталья',
  'minigame.exam.geo.6': 'Столица Индии?',
  'minigame.exam.geo.6.0': 'Мумбаи',
  'minigame.exam.geo.6.1': 'Калькутта',
  'minigame.exam.geo.6.2': 'Нью-Дели',
  'minigame.exam.geo.6.3': 'Ченнаи',
  'minigame.exam.geo.7': 'Столица Египта?',
  'minigame.exam.geo.7.0': 'Каир',
  'minigame.exam.geo.7.1': 'Александрия',
  'minigame.exam.geo.7.2': 'Луксор',
  'minigame.exam.geo.7.3': 'Гиза',
  'minigame.exam.geo.8': 'Столица Нигерии?',
  'minigame.exam.geo.8.0': 'Лагос',
  'minigame.exam.geo.8.1': 'Абуджа',
  'minigame.exam.geo.8.2': 'Кано',
  'minigame.exam.geo.8.3': 'Ибадан',
  'minigame.exam.geo.9': 'Столица Казахстана?',
  'minigame.exam.geo.9.0': 'Алматы',
  'minigame.exam.geo.9.1': 'Шымкент',
  'minigame.exam.geo.9.2': 'Астана',
  'minigame.exam.geo.9.3': 'Караганда',
  'minigame.exam.geo.10': 'Столица США?',
  'minigame.exam.geo.10.0': 'Нью-Йорк',
  'minigame.exam.geo.10.1': 'Вашингтон',
  'minigame.exam.geo.10.2': 'Лос-Анджелес',
  'minigame.exam.geo.10.3': 'Чикаго',
  'minigame.exam.geo.11': 'Столица Китая?',
  'minigame.exam.geo.11.0': 'Шанхай',
  'minigame.exam.geo.11.1': 'Гонконг',
  'minigame.exam.geo.11.2': 'Пекин',
  'minigame.exam.geo.11.3': 'Гуанчжоу',
  'minigame.exam.geo.12': 'Столица Германии?',
  'minigame.exam.geo.12.0': 'Мюнхен',
  'minigame.exam.geo.12.1': 'Гамбург',
  'minigame.exam.geo.12.2': 'Франкфурт',
  'minigame.exam.geo.12.3': 'Берлин',
  'minigame.exam.geo.13': 'Столица Италии?',
  'minigame.exam.geo.13.0': 'Рим',
  'minigame.exam.geo.13.1': 'Милан',
  'minigame.exam.geo.13.2': 'Неаполь',
  'minigame.exam.geo.13.3': 'Турин',
  'minigame.exam.geo.14': 'Столица Испании?',
  'minigame.exam.geo.14.0': 'Барселона',
  'minigame.exam.geo.14.1': 'Мадрид',
  'minigame.exam.geo.14.2': 'Севилья',
  'minigame.exam.geo.14.3': 'Валенсия',
  'minigame.exam.geo.15': 'Столица Мексики?',
  'minigame.exam.geo.15.0': 'Гвадалахара',
  'minigame.exam.geo.15.1': 'Монтеррей',
  'minigame.exam.geo.15.2': 'Мехико',
  'minigame.exam.geo.15.3': 'Канкун',
  'minigame.exam.geo.16': 'Столица Аргентины?',
  'minigame.exam.geo.16.0': 'Буэнос-Айрес',
  'minigame.exam.geo.16.1': 'Кордова',
  'minigame.exam.geo.16.2': 'Росарио',
  'minigame.exam.geo.16.3': 'Мендоса',
  'minigame.exam.geo.17': 'Столица Вьетнама?',
  'minigame.exam.geo.17.0': 'Хошимин',
  'minigame.exam.geo.17.1': 'Дананг',
  'minigame.exam.geo.17.2': 'Ханой',
  'minigame.exam.geo.17.3': 'Хюэ',
  'minigame.exam.geo.18': 'Административная столица ЮАР?',
  'minigame.exam.geo.18.0': 'Йоханнесбург',
  'minigame.exam.geo.18.1': 'Кейптаун',
  'minigame.exam.geo.18.2': 'Дурбан',
  'minigame.exam.geo.18.3': 'Претория',
  'minigame.exam.geo.19': 'Столица Швейцарии?',
  'minigame.exam.geo.19.0': 'Цюрих',
  'minigame.exam.geo.19.1': 'Женева',
  'minigame.exam.geo.19.2': 'Берн',
  'minigame.exam.geo.19.3': 'Базель',
  'minigame.exam.math.0': 'Сколько будет 7 + 8?',
  'minigame.exam.math.0.0': '14',
  'minigame.exam.math.0.1': '15',
  'minigame.exam.math.0.2': '16',
  'minigame.exam.math.0.3': '13',
  'minigame.exam.math.1': 'Сколько будет 9 × 6?',
  'minigame.exam.math.1.0': '54',
  'minigame.exam.math.1.1': '56',
  'minigame.exam.math.1.2': '63',
  'minigame.exam.math.1.3': '45',
  'minigame.exam.math.2': 'Сколько будет 100 − 37?',
  'minigame.exam.math.2.0': '73',
  'minigame.exam.math.2.1': '67',
  'minigame.exam.math.2.2': '63',
  'minigame.exam.math.2.3': '57',
  'minigame.exam.math.3': 'Половина от 90?',
  'minigame.exam.math.3.0': '40',
  'minigame.exam.math.3.1': '35',
  'minigame.exam.math.3.2': '50',
  'minigame.exam.math.3.3': '45',
  'minigame.exam.math.4': 'Сколько будет 12 × 12?',
  'minigame.exam.math.4.0': '124',
  'minigame.exam.math.4.1': '144',
  'minigame.exam.math.4.2': '132',
  'minigame.exam.math.4.3': '112',
  'minigame.exam.math.5': 'Сколько будет 81 ÷ 9?',
  'minigame.exam.math.5.0': '8',
  'minigame.exam.math.5.1': '7',
  'minigame.exam.math.5.2': '9',
  'minigame.exam.math.5.3': '6',
  'minigame.exam.math.6': 'Сколько будет 25 + 38?',
  'minigame.exam.math.6.0': '63',
  'minigame.exam.math.6.1': '53',
  'minigame.exam.math.6.2': '62',
  'minigame.exam.math.6.3': '65',
  'minigame.exam.math.7': 'Четверть от 200?',
  'minigame.exam.math.7.0': '25',
  'minigame.exam.math.7.1': '100',
  'minigame.exam.math.7.2': '50',
  'minigame.exam.math.7.3': '75',
  'minigame.exam.math.8': 'Сколько будет 15 × 4?',
  'minigame.exam.math.8.0': '45',
  'minigame.exam.math.8.1': '50',
  'minigame.exam.math.8.2': '60',
  'minigame.exam.math.8.3': '65',
  'minigame.exam.math.9': 'Сколько будет 1000 − 250?',
  'minigame.exam.math.9.0': '650',
  'minigame.exam.math.9.1': '700',
  'minigame.exam.math.9.2': '750',
  'minigame.exam.math.9.3': '850',
  'minigame.exam.math.10': 'Сколько будет 3 × 3 × 3?',
  'minigame.exam.math.10.0': '27',
  'minigame.exam.math.10.1': '9',
  'minigame.exam.math.10.2': '18',
  'minigame.exam.math.10.3': '36',
  'minigame.exam.math.11': '10 % от 300?',
  'minigame.exam.math.11.0': '3',
  'minigame.exam.math.11.1': '30',
  'minigame.exam.math.11.2': '60',
  'minigame.exam.math.11.3': '90',
  'minigame.exam.math.12': 'Сколько минут в двух часах?',
  'minigame.exam.math.12.0': '100',
  'minigame.exam.math.12.1': '120',
  'minigame.exam.math.12.2': '140',
  'minigame.exam.math.12.3': '200',
  'minigame.exam.math.13': 'Сколько будет 48 ÷ 6?',
  'minigame.exam.math.13.0': '6',
  'minigame.exam.math.13.1': '7',
  'minigame.exam.math.13.2': '8',
  'minigame.exam.math.13.3': '9',
  'minigame.exam.math.14': 'Сдача с 500 за покупку на 320?',
  'minigame.exam.math.14.0': '160',
  'minigame.exam.math.14.1': '170',
  'minigame.exam.math.14.2': '180',
  'minigame.exam.math.14.3': '190',
  'minigame.exam.math.15': 'Сколько дней в трёх неделях?',
  'minigame.exam.math.15.0': '21',
  'minigame.exam.math.15.1': '14',
  'minigame.exam.math.15.2': '18',
  'minigame.exam.math.15.3': '24',
  'minigame.exam.math.16': 'Сколько будет 19 + 23?',
  'minigame.exam.math.16.0': '42',
  'minigame.exam.math.16.1': '41',
  'minigame.exam.math.16.2': '43',
  'minigame.exam.math.16.3': '32',
  'minigame.exam.math.17': 'Какая дробь больше?',
  'minigame.exam.math.17.0': '3/4',
  'minigame.exam.math.17.1': '1/2',
  'minigame.exam.math.17.2': '2/3',
  'minigame.exam.math.17.3': '1/4',
  'minigame.exam.math.18': 'Сколько будет 5 × 11?',
  'minigame.exam.math.18.0': '51',
  'minigame.exam.math.18.1': '56',
  'minigame.exam.math.18.2': '55',
  'minigame.exam.math.18.3': '50',
  'minigame.exam.math.19': 'Треть от 60?',
  'minigame.exam.math.19.0': '15',
  'minigame.exam.math.19.1': '30',
  'minigame.exam.math.19.2': '20',
  'minigame.exam.math.19.3': '25',
  'minigame.exam.price.0': 'Что обычно дороже?',
  'minigame.exam.price.0.0': 'Буханка хлеба',
  'minigame.exam.price.0.1': 'Автомобиль',
  'minigame.exam.price.0.2': 'Пара носков',
  'minigame.exam.price.0.3': 'Карандаш',
  'minigame.exam.price.1': 'Что обычно дороже?',
  'minigame.exam.price.1.0': 'Квартира',
  'minigame.exam.price.1.1': 'Велосипед',
  'minigame.exam.price.1.2': 'Стул',
  'minigame.exam.price.1.3': 'Чайник',
  'minigame.exam.price.2': 'Что обычно дороже?',
  'minigame.exam.price.2.0': 'Яблоко',
  'minigame.exam.price.2.1': 'Билет в кино',
  'minigame.exam.price.2.2': 'Холодильник',
  'minigame.exam.price.2.3': 'Шапка',
  'minigame.exam.price.3': 'Что обычно дороже?',
  'minigame.exam.price.3.0': 'Зубная щётка',
  'minigame.exam.price.3.1': 'Коробок спичек',
  'minigame.exam.price.3.2': 'Ложка',
  'minigame.exam.price.3.3': 'Стиральная машина',
  'minigame.exam.price.4': 'Что обычно дороже?',
  'minigame.exam.price.4.0': 'Смартфон',
  'minigame.exam.price.4.1': 'Пачка соли',
  'minigame.exam.price.4.2': 'Пуговица',
  'minigame.exam.price.4.3': 'Тетрадь',
  'minigame.exam.price.5': 'Что обычно дороже?',
  'minigame.exam.price.5.0': 'Билет на трамвай',
  'minigame.exam.price.5.1': 'Литр молока',
  'minigame.exam.price.5.2': 'Телевизор',
  'minigame.exam.price.5.3': 'Кусок мыла',
  'minigame.exam.price.6': 'Что обычно дороже?',
  'minigame.exam.price.6.0': 'Батон',
  'minigame.exam.price.6.1': 'Мотоцикл',
  'minigame.exam.price.6.2': 'Свечка',
  'minigame.exam.price.6.3': 'Открытка',
  'minigame.exam.price.7': 'Что обычно дороже?',
  'minigame.exam.price.7.0': 'Корова',
  'minigame.exam.price.7.1': 'Курица',
  'minigame.exam.price.7.2': 'Яйцо',
  'minigame.exam.price.7.3': 'Килограмм риса',
  'minigame.exam.price.8': 'Что обычно дороже?',
  'minigame.exam.price.8.0': 'Бутылка воды',
  'minigame.exam.price.8.1': 'Пара обуви',
  'minigame.exam.price.8.2': 'Жвачка',
  'minigame.exam.price.8.3': 'Пачка салфеток',
  'minigame.exam.price.9': 'Что обычно дороже?',
  'minigame.exam.price.9.0': 'Авиабилет за границу',
  'minigame.exam.price.9.1': 'Проездной на месяц',
  'minigame.exam.price.9.2': 'Обед в столовой',
  'minigame.exam.price.9.3': 'Чашка кофе',
  'minigame.exam.price.10': 'Что обычно дороже?',
  'minigame.exam.price.10.0': 'Мешок цемента',
  'minigame.exam.price.10.1': 'Дом',
  'minigame.exam.price.10.2': 'Лопата',
  'minigame.exam.price.10.3': 'Ведро',
  'minigame.exam.price.11': 'Что обычно дороже?',
  'minigame.exam.price.11.0': 'Ноутбук',
  'minigame.exam.price.11.1': 'Ручка',
  'minigame.exam.price.11.2': 'Пакет сахара',
  'minigame.exam.price.11.3': 'Конверт',
  'minigame.exam.price.12': 'Что обычно дороже?',
  'minigame.exam.price.12.0': 'Год учёбы в университете',
  'minigame.exam.price.12.1': 'Учебник',
  'minigame.exam.price.12.2': 'Тетрадь',
  'minigame.exam.price.12.3': 'Линейка',
  'minigame.exam.price.13': 'Что обычно дороже?',
  'minigame.exam.price.13.0': 'Стрижка',
  'minigame.exam.price.13.1': 'Трактор',
  'minigame.exam.price.13.2': 'Пакет семян',
  'minigame.exam.price.13.3': 'Бутылка масла',
  'minigame.exam.price.14': 'Что обычно дороже?',
  'minigame.exam.price.14.0': 'Пачка чая',
  'minigame.exam.price.14.1': 'Швейная машинка',
  'minigame.exam.price.14.2': 'Катушка ниток',
  'minigame.exam.price.14.3': 'Иголка',
  'minigame.exam.price.15': 'Что обычно дороже?',
  'minigame.exam.price.15.0': 'Кольцо с бриллиантом',
  'minigame.exam.price.15.1': 'Шнурки',
  'minigame.exam.price.15.2': 'Спички',
  'minigame.exam.price.15.3': 'Жетон на метро',
  'minigame.exam.price.16': 'Что обычно дороже?',
  'minigame.exam.price.16.0': 'Ремонт крыши',
  'minigame.exam.price.16.1': 'Гвоздь',
  'minigame.exam.price.16.2': 'Молоток',
  'minigame.exam.price.16.3': 'Банка краски',
  'minigame.exam.price.17': 'Что обычно дороже?',
  'minigame.exam.price.17.0': 'Операция на сердце',
  'minigame.exam.price.17.1': 'Таблетка аспирина',
  'minigame.exam.price.17.2': 'Бинт',
  'minigame.exam.price.17.3': 'Градусник',
  'minigame.exam.price.18': 'Что обычно дороже?',
  'minigame.exam.price.18.0': 'Корабль',
  'minigame.exam.price.18.1': 'Лодка',
  'minigame.exam.price.18.2': 'Весло',
  'minigame.exam.price.18.3': 'Спасательный круг',
  'minigame.exam.price.19': 'Что обычно дороже?',
  'minigame.exam.price.19.0': 'Кирпич',
  'minigame.exam.price.19.1': 'Футбольный мяч',
  'minigame.exam.price.19.2': 'Пианино',
  'minigame.exam.price.19.3': 'Кусок мела',

  // Женские имена.
  'name.f.anna': 'Анна',
  'name.f.maria': 'Мария',
  'name.f.sofia': 'София',
  'name.f.elena': 'Елена',
  'name.f.sara': 'Сара',
  'name.f.laila': 'Лейла',
  'name.f.amira': 'Амира',
  'name.f.fatima': 'Фатима',
  'name.f.aisha': 'Аиша',
  'name.f.priya': 'Прия',
  'name.f.mei': 'Мэй',
  'name.f.yuna': 'Юна',
  'name.f.hana': 'Хана',
  'name.f.lucia': 'Лусия',
  'name.f.camila': 'Камила',
  'name.f.emma': 'Эмма',
  'name.f.olivia': 'Оливия',
  'name.f.nia': 'Ниа',
  'name.f.zara': 'Зара',
  'name.f.ada': 'Ада',

  // Мужские имена.
  'name.m.alexander': 'Александр',
  'name.m.ivan': 'Иван',
  'name.m.daniel': 'Даниэль',
  'name.m.david': 'Давид',
  'name.m.adam': 'Адам',
  'name.m.omar': 'Омар',
  'name.m.ali': 'Али',
  'name.m.yusuf': 'Юсуф',
  'name.m.ahmed': 'Ахмед',
  'name.m.arjun': 'Арджун',
  'name.m.wei': 'Вэй',
  'name.m.kenji': 'Кэндзи',
  'name.m.minjun': 'Минджун',
  'name.m.mateo': 'Матео',
  'name.m.santiago': 'Сантьяго',
  'name.m.noah': 'Ноа',
  'name.m.liam': 'Лиам',
  'name.m.kwame': 'Кваме',
  'name.m.tariq': 'Тарик',
  'name.m.leo': 'Лео',

  // Интерфейс клиента (apps/web): общие и стартовый экран.
  'ui.app.title': 'Один из восьми миллиардов',
  'ui.start.eyebrow': 'Одиночная жизнь',
  'ui.start.counter': '1 из 8 000 000 000',
  'ui.start.lead': 'Ты не выбирал, где родиться. Страна, год и семья выпадают по статистике — а дальше решаешь ты.',
  'ui.start.born': 'Родиться',
  'ui.start.continue': 'Продолжить жизнь',
  'ui.start.settings': 'Перед рождением',
  'ui.start.epoch': 'Эпоха',
  'ui.start.draw': 'Жребий',
  'ui.start.duration': 'Длительность',
  'ui.start.skipHeavy': 'Пропускать тяжёлое',
  'ui.start.skipHeavy.hint': 'Тексты мягче, статистика та же. Помощь остаётся.',
  'ui.start.realCountry': 'Страна, где я живу',
  'ui.start.realCountry.none': 'Не указывать',
  'ui.start.realCountry.hint': 'Только для сравнения в конце. Хранится на этом устройстве.',
  'ui.start.shelf': 'Моя полка',
  'ui.start.shelf.item': '{name} · {country} · {age} {years}',
  'ui.start.certificate': 'Свидетельство',
  'ui.start.music': 'Музыка',
  'ui.start.music.hint': 'Пока без звука: настройка сохраняется, звук появится позже.',
  'ui.start.load.corrupt': 'Сохранение в этом браузере повреждено, прочитать его не удалось. Можно начать заново.',
  'ui.start.load.version': 'Сохранение сделано другой версией игры и не читается. Можно начать заново.',

  // Три подсказки первой жизни (D1): фокус, карта жизни, конверт.
  'ui.hint.focus': 'Фокус года: выбери, на что уходит этот год. Один фокус на год, менять можно до его конца.',
  'ui.hint.map': 'Карта жизни: одна клетка — один год. Охра — сейчас. Пометить год ☆ — тап по строке журнала.',
  'ui.hint.envelope': 'Конверт: сбережения и долги семьи. Долг можно гасить, пока есть чем.',
  'ui.hint.close': 'Понятно',
  'ui.epoch.past': 'Прошлое',
  'ui.epoch.present': 'Настоящее',
  'ui.epoch.future': 'Будущее',
  'ui.epoch.any': 'Любая',
  'ui.draw.births': 'По рождениям',
  'ui.draw.population': 'По населению',
  'ui.start.mode': 'Как играть',
  'ui.mode.turn': 'По ходам',
  'ui.mode.observe': 'Наблюдать',
  'ui.mode.turn.hint': 'Год — ход: дела на энергию, решения без таймера, кнопка «Следующий год».',
  'ui.mode.observe.hint': 'Годы идут сами по часам, скорость ×1…×16; ты выбираешь фокус и отвечаешь на решения.',
  'ui.duration.30': '~30 минут',
  'ui.duration.60': '~1 час',
  'ui.duration.90': '~1,5 часа',
  'ui.theme.label': 'Тема',
  'ui.theme.system': 'Как в системе',
  'ui.theme.light': 'Светлая',
  'ui.theme.dark': 'Тёмная',

  // Экран рождения: жребий (A1) и карточка рождения.
  'ui.birth.lottery.eyebrow': 'Жребий',
  'ui.birth.lottery.title': 'Где ты родишься?',
  'ui.birth.lottery.lead': 'Каждая точка — страна. Шанс родиться в ней задаёт статистика, а не выбор. Нажми — и останется одна.',
  'ui.birth.lottery.alt': 'Карта стран точками',
  'ui.birth.draw': 'Родиться',
  'ui.birth.skip': 'Пропустить',
  'ui.birth.eyebrow': 'Запись о рождении',
  'ui.birth.name': 'Имя',
  'ui.birth.name.hint': 'Впиши своё или возьми одно из предложенных.',
  'ui.birth.name.suggest': 'Имена',
  'ui.birth.place': '{country} · {year}',
  'ui.birth.sex.f': 'девочка',
  'ui.birth.sex.m': 'мальчик',
  'ui.birth.urban': 'город',
  'ui.birth.rural': 'село',
  'ui.birth.children': '{order} из {total} детей',
  'ui.birth.children.only': 'единственный ребёнок в семье',
  'ui.birth.family': 'семья по достатку: {decile}',
  'ui.birth.decile.1': 'беднее почти всех в стране',
  'ui.birth.decile.2': 'среди самых бедных',
  'ui.birth.decile.3': 'заметно беднее среднего',
  'ui.birth.decile.4': 'чуть беднее среднего',
  'ui.birth.decile.5': 'ровно посередине',
  'ui.birth.decile.6': 'чуть выше среднего',
  'ui.birth.decile.7': 'заметно выше среднего',
  'ui.birth.decile.8': 'среди обеспеченных',
  'ui.birth.decile.9': 'среди богатых',
  'ui.birth.decile.10': 'богаче почти всех в стране',
  'ui.birth.lifeExp.f': 'ожидаемая продолжительность жизни для девочек: {n} {years}',
  'ui.birth.lifeExp.m': 'ожидаемая продолжительность жизни для мальчиков: {n} {years}',
  'ui.birth.start': 'Начать эту жизнь',
  'ui.birth.share': 'Поделиться',

  // Порядковые числительные (порядок рождения среди детей); после 10 — цифрой.
  'ui.ordinal.f.1': 'первая',
  'ui.ordinal.f.2': 'вторая',
  'ui.ordinal.f.3': 'третья',
  'ui.ordinal.f.4': 'четвёртая',
  'ui.ordinal.f.5': 'пятая',
  'ui.ordinal.f.6': 'шестая',
  'ui.ordinal.f.7': 'седьмая',
  'ui.ordinal.f.8': 'восьмая',
  'ui.ordinal.f.9': 'девятая',
  'ui.ordinal.f.10': 'десятая',
  'ui.ordinal.f.n': '{n}-я',
  'ui.ordinal.m.1': 'первый',
  'ui.ordinal.m.2': 'второй',
  'ui.ordinal.m.3': 'третий',
  'ui.ordinal.m.4': 'четвёртый',
  'ui.ordinal.m.5': 'пятый',
  'ui.ordinal.m.6': 'шестой',
  'ui.ordinal.m.7': 'седьмой',
  'ui.ordinal.m.8': 'восьмой',
  'ui.ordinal.m.9': 'девятый',
  'ui.ordinal.m.10': 'десятый',
  'ui.ordinal.m.n': '{n}-й',

  // Экран жизни.
  'ui.life.header': '{name} · {country}',
  'ui.life.speed': '×{n}',
  'ui.life.pause': 'Пауза',
  'ui.life.resume': 'Продолжить',
  'ui.life.waiting': 'Ждём решения',
  'ui.life.autopilot': 'Автопилот',
  'ui.life.focus': 'Фокус года',
  'ui.life.journal': 'Журнал',
  'ui.life.journal.earlier': 'Раньше',
  'ui.life.star': '☆',
  'ui.life.thought': 'Мысль',
  'ui.life.thought.hint': 'До 100 знаков. Видно только тебе; попадёт в свидетельство, если год помечен.',
  'ui.life.thought.save': 'Сохранить',
  'ui.life.thought.cancel': 'Отмена',
  'ui.life.map': 'Карта жизни',
  'ui.life.map.caption': 'одна клетка = один год · охра — сейчас',
  'ui.life.side': 'Карта и конверт',

  // Ход года (core-loop-v2): энергия, утро, места, «Следующий год».
  'ui.energy.label': 'энергия {n} из {max}',
  'ui.energy.full': 'Полные силы на этот год.',
  'ui.energy.reason.health': 'слабое здоровье',
  'ui.energy.reason.age': 'возраст',
  'ui.energy.reason.school': 'школа съедает время',
  'ui.energy.reason.toddler': 'маленький ребёнок',
  'ui.energy.reason.chronic': 'хроническая болезнь',
  'ui.morning.eyebrow': 'Утро года',
  'ui.morning.title': 'Год {year} · возраст {age}',
  'ui.morning.delta': '{name} {delta}',
  'ui.morning.deltaMoney': '{name} {delta} $',
  'ui.morning.noChange': 'За год ничего заметно не изменилось.',
  'ui.morning.go': 'К делам',
  'ui.stats.money': 'Деньги',
  'ui.place.unavailable': '{name} — {reason}',
  'ui.next.year': 'Следующий год',
  'ui.next.blocked': 'Следующий год — сначала ответь',
  'ui.next.hold': 'Удерживай, чтобы листать годы до ближайшего решения.',
  'ui.hint.turn.energy': 'Энергия: обычно три очка в год. Дело стоит очко, эффект виден сразу.',
  'ui.hint.turn.places': 'Куда пойти: второй визит в то же место даёт половину, третий — ничего.',
  'ui.hint.turn.next': '«Следующий год» проживает год: экономика, здоровье, событие. Удерживай, чтобы листать.',

  // Лента фокусов (A3): недоступное — причина в самом тексте кнопки.
  'ui.focus.unavailable': '{name} — {reason}',
  'ui.focus.restless': 'Метания: смена фокуса чаще трёх раз за год отнимает счастье.',
  'focus.reason.age': 'не по возрасту',
  'focus.reason.noJob': 'нет работы',
  'focus.reason.noCareTarget': 'некого опекать',

  // Конверт (A6): деньги спрятаны за тапом.
  'ui.envelope': 'Конверт',
  'ui.envelope.savings': 'Сбережения',
  'ui.envelope.debt': 'Долг',
  'ui.envelope.income': 'Доход в год',
  'ui.envelope.noJob': 'нет работы',
  'ui.envelope.pay': 'Погасить долг · {sum}',
  'ui.envelope.pay.noMoney': 'Погасить долг — нет денег',
  'ui.envelope.payQueued': 'В конце года погасим {sum}.',
  'ui.envelope.later': '{action} — появится позже',
  'ui.envelope.action.save': 'Отложить',
  'ui.envelope.action.send': 'Перевести',
  'ui.envelope.action.invest': 'Вложить',

  // Скорость.
  'ui.speed': 'Скорость',
  'ui.speed.skip': 'До следующего решения',

  // Каркас мини-игры (план 2b, Task 8): заголовки по id, таймер, «Пропустить».
  'ui.minigame.timer': 'осталось {s} с',
  'ui.minigame.skip': 'Пропустить',
  'ui.minigame.noScreen': 'Экран этой мини-игры ещё не готов — засчитаем ожидаемый исход.',
  'ui.minigame.bet': 'Ставка',
  'ui.minigame.bet.max': 'не больше {sum}',
  'ui.minigame.bet.none': 'ставить нечего, сбережений нет',
  'ui.minigame.invest.sum': 'Сумма',
  'ui.minigame.reveal.skip': 'Сразу к результату',
  'ui.minigame.lottery.ticket': 'Билет',
  'ui.minigame.lottery.jackpot': 'Джекпот',
  'ui.minigame.lottery.odds': 'Джекпот — 1 : {jackpot}; ×10 — 1 : {x10}; вернуть билет — 1 : {x1}.',
  'ui.minigame.lottery.card': 'Скретч-карта',
  'ui.minigame.lottery.cell': 'Клетка {n}',
  'ui.minigame.lottery.scratched': 'стёрто',
  'ui.minigame.lottery.hint': 'Стирайте клетки. Билет куплен, когда стёрты все шесть; итог — в журнале в конце года.',
  'ui.minigame.lottery.scratchAll': 'Стереть все',
  'ui.minigame.lottery.noMoney': 'На билет не хватает',
  'ui.minigame.roulette.kinds': 'Вид ставки',
  'ui.minigame.roulette.kind.color': 'Цвет · {p} из {n} · ×{x}',
  'ui.minigame.roulette.kind.dozen': 'Дюжина · {p} из {n} · ×{x}',
  'ui.minigame.roulette.kind.number': 'Число · {p} из {n} · ×{x}',
  'ui.minigame.roulette.kind.color.short': 'Цвет',
  'ui.minigame.roulette.kind.dozen.short': 'Дюжина',
  'ui.minigame.roulette.red': 'Красное',
  'ui.minigame.roulette.black': 'Чёрное',
  'ui.minigame.roulette.dozen.0': '1–12',
  'ui.minigame.roulette.dozen.1': '13–24',
  'ui.minigame.roulette.dozen.2': '25–36',
  'ui.minigame.roulette.number': 'Число от 0 до 36',
  'ui.minigame.roulette.entry': 'Вход за вечер · {sum}, списывается с первым кругом.',
  'ui.minigame.roulette.spin': 'Крутить',
  'ui.minigame.roulette.spinning': 'Шарик бежит по {n} секторам…',
  'ui.minigame.roulette.closed': 'Казино закрывается',
  'ui.minigame.slots.payouts': 'Три семёрки ×{sevens} · три одинаковых ×{same} · две семёрки ×{two}. На барабане {symbols} символов, два из них пустые.',
  'ui.minigame.slots.spin': 'Крутить',
  'ui.minigame.slots.auto': 'Крутить ×{n}',
  'ui.minigame.slots.spinning': 'Барабаны крутятся…',
  'ui.minigame.slots.autoWarn': 'При автоповторе ×{n} тяга растёт вдвое быстрее: +{auto} вместо +{one}.',
  'ui.minigame.blackjack.rules': 'Дилер берёт до {stands}. Блэкджек с двух карт — ×{natural}. Без удвоений и сплитов.',
  'ui.minigame.blackjack.dealer': 'У дилера',
  'ui.minigame.blackjack.hand': 'Ваши карты',
  'ui.minigame.blackjack.hidden': 'закрыта',
  'ui.minigame.blackjack.value': 'сумма {n}',
  'ui.minigame.blackjack.hit': 'Ещё',
  'ui.minigame.blackjack.stand': 'Хватит',
  'ui.minigame.blackjack.hint': 'Подсказка ({n} рук сыграно): по базовой стратегии сейчас — «{action}».',
  'ui.minigame.blackjack.learning': 'Сыграно рук: {hands} из {n}. Подсказка откроется позже, пока — знания +1 за руку.',
  'ui.minigame.blackjack.betLocked': 'Ставка закреплена: {sum}.',
  'ui.minigame.dice.rules': 'Больше 7 — ставка вернётся; ровно 7 — ×{seven}; меньше — ставка потеряна.',
  'ui.minigame.dice.risk.robbery': 'Район неспокойный: с вероятностью {p} % после игры ограбят.',
  'ui.minigame.dice.risk.arrest': 'Игра здесь вне закона: с вероятностью {p} % — облава и штраф.',
  'ui.minigame.dice.roll': 'Бросить',
  'ui.minigame.bets.margin': 'Коэффициенты честные, маржа букмекера {p} %.',
  'ui.minigame.bets.commission': 'Через знакомого букмекера: комиссия {p} % с выплаты.',
  'ui.minigame.bets.pick': 'Выберите команду',
  'ui.minigame.bets.row': 'шанс {p} % · ×{odds}',
  'ui.minigame.bets.place': 'Поставить',
  'ui.minigame.pyramid.offer': 'Знакомый предлагает «вложение под {yield} % в год». Сколько вложить?',
  'ui.minigame.pyramid.sum': 'Вложить',
  'ui.minigame.pyramid.invest': 'Вложить {sum}',
  'ui.minigame.pyramid.decline': 'Отказаться',
  'ui.minigame.stocks.held': 'Акции сейчас стоят {sum}.',
  'ui.minigame.stocks.none': 'Акций пока нет.',
  'ui.minigame.stocks.buy': 'Купить на {sum}',
  'ui.minigame.stocks.sell': 'Продать всё · {sum}',
  'ui.minigame.crypto.hidden': 'Монета есть. Курс виден только по тапу, и каждый взгляд — тяга +{urge}.',
  'ui.minigame.crypto.held': 'Монета сейчас стоит {sum}.',
  'ui.minigame.crypto.none': 'Монеты пока нет.',
  'ui.minigame.crypto.view': 'Посмотреть курс',
  'ui.minigame.crypto.viewOnly': 'Только посмотреть и закрыть',
  'ui.minigame.crypto.buy': 'Купить на {sum}',
  'ui.minigame.crypto.sell': 'Продать всё · {sum}',
  'ui.minigame.loans.pawn': 'Ломбард · +{sum}, через год вернуть {due}',
  'ui.minigame.loans.micro': 'Микрозайм · +{sum}, через год вернуть {due}',
  'ui.envelope.busy': '{action} — в этом году уже сыграли',
  'ui.envelope.open.lottery': 'Лотерея',
  'ui.envelope.open.roulette': 'Казино: рулетка',
  'ui.envelope.open.slots': 'Казино: слоты',
  'ui.envelope.open.blackjack': 'Казино: блэкджек',
  'ui.envelope.open.dice': 'Кости',
  'ui.envelope.open.bets': 'Ставки',
  'ui.envelope.open.stocks': 'Вложить в бумаги',
  'ui.envelope.open.crypto': 'Вложить в монету',
  'ui.envelope.open.loans': 'Занять',
  'minigame.title.lottery': 'Лотерея',
  'minigame.title.roulette': 'Рулетка',
  'minigame.title.slots': 'Слоты',
  'minigame.title.blackjack': 'Блэкджек',
  'minigame.title.dice': 'Уличные кости',
  'minigame.title.bets': 'Ставка на матч',
  'minigame.title.pyramid': 'Пирамида',
  'minigame.title.crypto': 'Монета',
  'minigame.title.stocks': 'Бумаги',
  'minigame.title.loans': 'Ломбард и микрозайм',
  'minigame.title.exam': 'Экзамен',
  'minigame.title.interview': 'Собеседование',
  'minigame.title.bargain': 'Торг на рынке',
  'minigame.title.field': 'Сезон на поле',
  'minigame.title.driving': 'Экзамен на права',
  'minigame.title.busking': 'Уличная музыка',
  'minigame.title.cooking': 'Праздничный стол',
  'minigame.title.yard': 'Двор',
  'minigame.title.lesson': 'Урок',
  'minigame.title.bullying': 'Три реплики',
  'minigame.title.firstLove': 'Первое сообщение',
  'minigame.title.wedding': 'Свадебный стол',
  'minigame.title.night': 'Ночь',
  'minigame.title.talk': 'Разговор',
  'minigame.title.clinic': 'Очередь',
  'minigame.title.suitcase': 'Чемодан',
  'minigame.title.border': 'Граница',
  'minigame.title.shift': 'Смена',
  'minigame.title.budget': 'Собрать месяц',
  'minigame.title.refusal': 'Отказ',
  'minigame.title.legacy': 'Что оставить',
  'minigame.title.memory': 'Память',

  // Экраны разделов F и G (план 2b, Task 10): примитивы и подписи.
  'ui.minigame.hint': 'подсказка',
  'ui.minigame.chance': 'шанс {p} %',
  'ui.minigame.continue': 'Дальше',
  'ui.minigame.order.up': 'Выше',
  'ui.minigame.order.down': 'Ниже',
  'ui.minigame.order.up.short': '↑',
  'ui.minigame.order.down.short': '↓',
  'ui.minigame.tap.start': 'Начать',
  'ui.minigame.tap.tap': 'Тап',
  'ui.minigame.tap.beat': 'такт {n} из {total}',
  'ui.minigame.tap.count': 'тапов: {n}',
  'ui.minigame.tap.stop': 'Закончить',
  'ui.minigame.hold.hint': 'Удерживайте кнопку {s} с: пальцем, мышью или пробелом.',
  'ui.minigame.hold.escaped': 'Кнопка уехала — нажмите её снова за {s} с.',
  'ui.minigame.pairs.open': 'Карточка {n}',
  'ui.minigame.pairs.found': 'найдено пар: {n} из {total}',
  'ui.minigame.pairs.done': 'Закончить',
  'ui.minigame.pick.count': 'выбрано {n} из {max}',
  'ui.minigame.exam.rules': 'Пять вопросов, сдано при {pass} из {total} верных. Погашено неверных на вопрос: {hidden}.',
  'ui.minigame.exam.q': 'Вопрос {n}',
  'ui.minigame.exam.hidden': 'вспомнили: не то',
  'ui.minigame.exam.submit': 'Сдать',
  'ui.minigame.interview.rules': 'Место уровня {level}: все три ответа в тон — уровень +1, два мимо — −1.',
  'ui.minigame.interview.change': 'Предлагают место уровня {level}, сейчас — {current}. Все три ответа в тон — уровень +1, два мимо — −1.',
  'ui.minigame.interview.submit': 'Ответить',
  'ui.minigame.bargain.price': 'Продавец просит {sum}.',
  'ui.minigame.bargain.zone': 'Уступит до {p} % по отношениям; вдвое ниже — уйдёт. Попыток: {n}.',
  'ui.minigame.bargain.again': 'Продавец качает головой. Осталось попыток: {n}.',
  'ui.minigame.bargain.offer': 'Ваша цена',
  'ui.minigame.bargain.send': 'Предложить ({n})',
  'ui.minigame.field.stake': 'Посев на {sum}: три клетки по {cell}.',
  'ui.minigame.field.drought': 'Засуха: урожай ×{a} / ×{b} / ×{c} по культурам.',
  'ui.minigame.field.cell': 'Клетка {n}',
  'ui.minigame.field.crop': 'в среднем {mean} %, разброс ±{sigma} %',
  'ui.minigame.field.sow': 'Посеять',
  'ui.minigame.driving.rules': 'Экзамен стоит {sum}, сдано при {pass} из {total}. С правами риск аварии ×{mult}, работа уровнем +{bonus}.',
  'ui.minigame.driving.submit': 'Сдать',
  'ui.minigame.busking.rules': 'Тапайте в такт: {beats} ударов за {s} с. В футляр +{sum} и счастье +{h} в любом случае; точность — только размер толпы.',
  'ui.minigame.cooking.rules': 'Выберите {n} из {total}: первый — главный. Правильного ответа нет, отношения +{r}.',
  'ui.minigame.cooking.label': 'Ингредиенты',
  'ui.minigame.cooking.cook': 'Готовить',
  'ui.minigame.yard.rules': 'Позовите {n} из {cells}: каждый согласится с шансом {p} %.',
  'ui.minigame.yard.label': 'Дети во дворе',
  'ui.minigame.yard.child': 'Ребёнок {n}',
  'ui.minigame.yard.call': 'Позвать',
  'ui.minigame.lesson.rules': 'Расставьте по интересу: сильный учитель первым — знания +{top}, вторым — +{second}.',
  'ui.minigame.lesson.label': 'Предметы',
  'ui.minigame.lesson.strongNote': 'сильный учитель',
  'ui.minigame.lesson.submit': 'Готово',
  'ui.minigame.bullying.support': 'В этой стране взрослые чаще помогают: шанс у «рассказать» выше.',
  'ui.minigame.bullying.hidden': 'Тяжёлая тема скрыта: засчитаем обычный исход.',
  'ui.minigame.bullying.submit': 'Ответить',
  'ui.minigame.firstLove.rules': 'Три части, по четыре варианта. Совпадений с характером 0–3 → отношения +{a}/+{b}/+{c}/+{d}.',
  'ui.minigame.firstLove.part.greeting': 'Обращение',
  'ui.minigame.firstLove.part.core': 'Суть',
  'ui.minigame.firstLove.part.signature': 'Подпись',
  'ui.minigame.firstLove.send': 'Отправить',
  'ui.minigame.wedding.rules': 'Выберите {n} из {total}. Счастье {base} + радость, не больше {max}. Родители покроют {help} % с шансом {p} %.',
  'ui.minigame.wedding.label': 'Расходы',
  'ui.minigame.wedding.item': '{sum} · счастье +{joy}',
  'ui.minigame.wedding.total': 'итого {sum} · счастье +{joy} · сбережения {money}',
  'ui.minigame.wedding.confirm': 'Праздновать',
  'ui.minigame.night.rules': 'Укачать: {beats} такта плача за {s} с, окно ±{w} мс. Любой промах — здоровье {h}, ребёнку ничего не грозит.',
  'ui.minigame.night.rock': 'Укачать',
  'ui.minigame.talk.rules': 'Услышит — отношения +{r}, холодно — −{r}.',
  'ui.minigame.talk.hint': 'он услышит',
  'ui.minigame.clinic.rules': 'Сбережения {money}. Выберите окно.',
  'ui.minigame.clinic.label': 'Окошки',
  'ui.minigame.clinic.window': 'ждать {days} дн. · {sum} · здоровье +{h}',
  'ui.minigame.clinic.none': 'здоровье {h}',
  'ui.minigame.clinic.closedP': 'врача может не быть, {p} %',
  'ui.minigame.clinic.noMoney': 'не хватает денег',
  'ui.minigame.suitcase.rules': 'Возьмите {n} из {total}. Что даёт вещь — написано на ней.',
  'ui.minigame.suitcase.label': 'Вещи',
  'ui.minigame.suitcase.go': 'В дорогу',
  'ui.minigame.suitcase.stay': 'Остаться',
  'ui.minigame.border.rules': 'Все ответы точные — шанс +{p} %, все мимо — −{p} %.',
  'ui.minigame.border.answer': 'Ответить',
  'ui.minigame.border.stay': 'Остаться',
  'ui.minigame.shift.rules': '{kind}: точность выше {sharp} % — шанс повышения +{bonus} %, ниже {tired} % — здоровье {h}.',
  'ui.minigame.shift.field.rules': 'Соберите спелые клетки и не трогайте зелёные.',
  'ui.minigame.shift.ripe': 'Спелая',
  'ui.minigame.shift.green': 'Зелёная',
  'ui.minigame.shift.field.done': 'Собрано',
  'ui.minigame.shift.factory.rules': 'Конвейер: {beats} ударов за {s} с.',
  'ui.minigame.shift.office.rules': 'Разложите карточки по двум стопкам.',
  'ui.minigame.shift.office.done': 'Разложено',
  'ui.minigame.shift.trade.rules': 'Посчитайте сдачу.',
  'ui.minigame.shift.trade.sum': 'Цена {price}, дали {paid}. Сдача?',
  'ui.minigame.shift.trade.done': 'Выдать',
  'ui.minigame.budget.rules': 'В месяц {sum}. Вычеркните {n} статьи — что вычеркнете, то и будет.',
  'ui.minigame.budget.label': 'Статьи',
  'ui.minigame.budget.submit': 'Собрать месяц',
  'ui.minigame.refusal.rules': 'Сила тяги {n} из 100. Удержите {s} с: тяга {ok}; сорвалось — +{fail}.',
  'ui.minigame.refusal.escape': 'При тяге выше {th} кнопка уезжает — поймайте её за {s} с.',
  'ui.minigame.legacy.rules': 'Каждое — детям или когорте.',
  'ui.minigame.legacy.rules.single': 'Детей нет: всё — {to}.',
  'ui.minigame.legacy.retire': 'На пенсию',
  'ui.minigame.legacy.stay': 'Работать дальше',
  'ui.minigame.memory.rules': 'Найдите {n} пары воспоминаний. Все три — счастье +{h}; иначе ничего плохого.',
  'ui.minigame.memory.drift': 'Карточки плывут: память уже не та.',
  'ui.minigame.memory.label': 'Воспоминания',
  'ui.minigame.memory.age': '{ageAt}',

  // Карточка решения (A4, A9).
  'ui.decision.unavailable': '{name} — {reason}',
  'ui.decision.hint': 'Что это значит?',
  'ui.decision.auto': 'автопилот выберет «{option}» через {s} с',
  'ui.decision.intercept': 'Перехватить',

  // Свидетельство о смерти (A10, G16) и карточки для шеринга (C5).
  'ui.death.eyebrow': 'Запись о смерти',
  'ui.death.span': '{from} → {to} · {born} — {died}',
  'ui.death.span.same': '{country} · {born} — {died}',
  'ui.death.lived': 'Прожито',
  'ui.death.cause': 'Причина',
  'ui.death.cause.hidden': 'не называем',
  'ui.death.education': 'Образование',
  'ui.death.work': 'Работа',
  'ui.death.work.none': 'не было',
  'ui.death.work.years': '{title}, {n} {years}',
  'ui.death.work.retired': '{n} {years}, потом пенсия',
  'ui.death.family': 'Семья',
  'ui.death.family.none': 'без семьи',
  'ui.death.family.partner.f': 'муж',
  'ui.death.family.partner.m': 'жена',
  'ui.death.family.children': '{n} {word}',
  'ui.death.family.both': '{partner}, {children}',
  'ui.death.moves': 'Переезды',
  'ui.death.moves.none': 'не было',
  'ui.death.moves.n': '{n}',
  'ui.death.map': 'Карта жизни',
  'ui.death.keyYears': 'Важные годы',
  'ui.death.keyYears.hint': 'Три года, которые войдут в мемориал: из помеченных ☆ или любые прожитые.',
  'ui.death.keyYears.empty': '—',
  'ui.death.keyYears.pick': 'Год {age}',
  'ui.death.keyYears.any': 'Любой прожитый год',
  'ui.death.keyYears.add': 'Добавить',
  'ui.death.lastWords': 'Последние слова',
  'ui.death.lastWords.hint': 'Одна строка на свидетельстве. До {max} знаков.',
  'ui.death.lastWords.count': '{n} / {max}',
  'ui.death.compare.own': 'Ожидалось для родившихся в {country} в {year}: {n} {years}',
  'ui.death.compare.real': 'Для родившихся в том же году в {country}: {n} {years}',
  'ui.death.share': 'Поделиться',
  'ui.death.again': 'Родиться снова',
  'ui.death.back': 'Назад',
  'ui.death.share.fail': 'Не удалось собрать карточку.',
  'ui.death.dream': 'Мечта',
  'ui.death.dream.done': '{dream} — сбылась в {age} {years}',
  'ui.death.dream.failed': '{dream} — не сбылась',
  'ui.death.milestones': 'Вехи',

  // Мечта и цель года на экране жизни (core-loop-v2 §4, план 2d Task 6).
  'ui.dream.eyebrow': 'Мечта',
  'ui.dream.title': 'О чём мечтать?',
  'ui.dream.lead': 'Три мечты под твою страну и семью. Одна — на всю жизнь: она будет в шапке и в свидетельстве. Шансов она не меняет.',
  'ui.dream.done': 'Мечта сбылась: {dream}',
  'ui.goal.done': 'выполнена',

  // Личное дело (H1): документ в стиле свидетельства.
  'ui.dossier.open': 'Личное дело: {name}',
  'ui.dossier.eyebrow': 'Личное дело',
  'ui.dossier.back': 'Назад к жизни',
  'ui.dossier.section.person': 'Кто',
  'ui.dossier.section.look': 'Внешность',
  'ui.dossier.section.family': 'Семья',
  'ui.dossier.section.study': 'Образование',
  'ui.dossier.section.work': 'Работа',
  'ui.dossier.section.money': 'Деньги',
  'ui.dossier.section.stats': 'Состояния',
  'ui.dossier.section.health': 'Привычки и здоровье',
  'ui.dossier.section.assets': 'Документы и имущество',
  'ui.dossier.section.goals': 'Мечта и вехи',
  'ui.dossier.section.map': 'Карта жизни',
  'ui.dossier.sex': 'Пол',
  'ui.dossier.sex.f': 'женский',
  'ui.dossier.sex.m': 'мужской',
  'ui.dossier.age': 'Возраст',
  'ui.dossier.age.value': '{n} {years} · {year}',
  'ui.dossier.country': 'Страна',
  'ui.dossier.country.from': '{country}, родом из {from}',
  'ui.dossier.place': 'Где живёт',
  'ui.dossier.height': 'Рост',
  'ui.dossier.height.value': '{n} см',
  'ui.dossier.build': 'Телосложение',
  'ui.dossier.hair': 'Волосы',
  'ui.dossier.eyes': 'Глаза',
  'ui.dossier.mark': 'Примета',
  'ui.dossier.parents': 'Родители',
  'ui.dossier.parents.2': 'оба живы',
  'ui.dossier.parents.1': 'один из родителей жив',
  'ui.dossier.parents.0': 'никого не осталось',
  'ui.dossier.siblings': 'Братья и сёстры',
  'ui.dossier.partner': 'Партнёр',
  'ui.dossier.children': 'Дети',
  'ui.dossier.children.list': '{n} {word}: {ages}',
  'ui.dossier.children.age': '{n} {years}',
  'ui.dossier.none': 'нет',
  'ui.dossier.education': 'Образование',
  'ui.dossier.school': 'Учёба сейчас',
  'ui.dossier.school.yes': 'учится',
  'ui.dossier.school.no': 'не учится',
  'ui.dossier.job': 'Должность',
  'ui.dossier.job.none': 'нет работы',
  'ui.dossier.job.level': 'Уровень',
  'ui.dossier.job.years.label': 'Стаж',
  'ui.dossier.job.years': '{n} {years}',
  'ui.dossier.income': 'Доход в год',
  'ui.dossier.savings': 'Сбережения',
  'ui.dossier.debt': 'Долг',
  'ui.dossier.expenses': 'Расходы в год',
  'ui.dossier.stat.value': '{n} — {word}',
  'ui.dossier.stat.health.low': 'сил мало, часто нездоровится',
  'ui.dossier.stat.health.mid': 'бывает одышка',
  'ui.dossier.stat.health.high': 'крепкое здоровье',
  'ui.dossier.stat.happiness.low': 'тяжело на душе',
  'ui.dossier.stat.happiness.mid': 'по-разному',
  'ui.dossier.stat.happiness.high': 'жизнь нравится',
  'ui.dossier.stat.knowledge.low': 'учиться не довелось',
  'ui.dossier.stat.knowledge.mid': 'знает своё дело',
  'ui.dossier.stat.knowledge.high': 'много знает',
  'ui.dossier.stat.relations.low': 'почти один',
  'ui.dossier.stat.relations.mid': 'есть с кем поговорить',
  'ui.dossier.stat.relations.high': 'близких много',
  'ui.dossier.smoking': 'Курение',
  'ui.dossier.alcohol': 'Алкоголь',
  'ui.dossier.gambling': 'Азарт',
  'ui.dossier.habit.yes': 'есть',
  'ui.dossier.habit.no': 'нет',
  'ui.dossier.craving.some': 'иногда тянет',
  'ui.dossier.craving.strong': 'тянет',
  'ui.dossier.conditions': 'Хронические болезни',
  'ui.dossier.condition.chronic': 'хроническая болезнь',
  'ui.dossier.condition.hiv': 'ВИЧ',
  'ui.dossier.condition.gambling': 'зависимость от игры',
  'ui.dossier.illness': 'сейчас болеет',
  'ui.dossier.license': 'Права',
  'ui.dossier.yes': 'есть',
  'ui.dossier.housing': 'Жильё',
  'ui.dossier.housing.own': 'своё',
  'ui.dossier.car': 'Машина',
  'ui.dossier.holdings': 'Вложения',
  'ui.dossier.holdings.stocks': 'акции {sum}',
  'ui.dossier.holdings.crypto': 'монета {sum}',
  'ui.dossier.holdings.pyramid': 'пирамида {sum}, активна',
  'ui.dossier.loan': 'Заём',
  'ui.dossier.loan.due': 'вернуть {sum} в следующем году',
  'ui.dossier.loan.defaulted': 'не возвращён, долг у коллекторов',
  'ui.dossier.dream': 'Мечта',
  'ui.dossier.dream.progress': '{dream} — {percent} %',
  'ui.dossier.milestones': 'Вехи',
  'ui.dossier.milestones.none': 'пока нет',
  'ui.dossier.starred': 'Помеченные годы',
  'ui.share.signature': 'Один из восьми миллиардов',
  'ui.share.birth.eyebrow': 'Запись о рождении',
  'ui.share.birth.place': '{country}, {year}',
  'ui.share.birth.lifeExp': 'ожидаемо: {n} {years}',
  'ui.share.death.eyebrow': 'Запись о смерти',
  'ui.share.keyYears': 'важные годы: {years}',
  'ui.share.file.birth': 'zapis-o-rozhdenii.png',
  'ui.share.file.death': 'svidetelstvo.png',
  'ui.word.child.one': 'ребёнок',
  'ui.word.child.few': 'ребёнка',
  'ui.word.child.many': 'детей',

  // Состояния (спецификация 4.2): подпись и пояснение в title.
  'ui.stats.health': 'Здоровье',
  'ui.stats.health.title': 'Влияет на риск смерти и способность работать. Растёт от спорта, осмотров и заботы, падает с возрастом, бедностью, курением и алкоголем.',
  'ui.stats.happiness': 'Счастье',
  'ui.stats.happiness.title': 'Растёт от дохода (с убывающей отдачей), близких и здоровья, падает от долгов, одиночества и утрат.',
  'ui.stats.knowledge': 'Знания',
  'ui.stats.knowledge.title': 'Влияют на экзамены, работу и зарплату. Потолок задают доступность школы и достаток семьи. Выше ~70 рост почти ничего не даёт.',
  'ui.stats.relations': 'Отношения',
  'ui.stats.relations.title': 'Семья и друзья. Влияют на брак, развод, буллинг и одиночество в старости.',

  // Досье H1: внешность и имущество (косметика; look.ts).
  'look.build.slim': 'худощавое',
  'look.build.average': 'обычное',
  'look.build.stout': 'крепкое',
  'look.hair.black': 'чёрные',
  'look.hair.dark': 'тёмные',
  'look.hair.brown': 'каштановые',
  'look.hair.fair': 'светлые',
  'look.hair.red': 'рыжие',
  'look.hair.grey': 'седые',
  'look.hair.bald': 'лысина',
  'look.eyes.brown': 'карие',
  'look.eyes.black': 'чёрные',
  'look.eyes.blue': 'голубые',
  'look.eyes.grey': 'серые',
  'look.eyes.green': 'зелёные',
  'look.mark.none': 'без особых примет',
  'look.mark.mole': 'родинка на щеке',
  'look.mark.freckles': 'веснушки',
  'look.mark.scar': 'шрам на брови',
  'look.mark.dimples': 'ямочки на щеках',
  'look.mark.gap': 'щербинка между зубами',
  'assets.housing.family': 'с семьёй',
  'assets.housing.rent': 'снимает',
  'assets.housing.own': 'Своё жильё: ключи от собственного дома.',

  // Места (H2, план 2d): названия, дела, ожидания словами, причины недоступности, записи журнала.
  'place.group.life': 'Жизнь',
  'place.group.money': 'Деньги',
  'place.group.risk': 'Риск',
  'place.work': 'Работа',
  'place.work.shift': 'Выйти в смену',
  'place.work.vacation': 'Взять отпуск',
  'place.jobcenter': 'Биржа труда',
  'place.jobcenter.find': 'Найти работу',
  'place.jobcenter.change': 'Сменить работу',
  'place.jobcenter.parttime': 'Подработка',
  'place.courses': 'Курсы и библиотека',
  'place.courses.study': 'Учиться',
  'place.gym': 'Спортзал',
  'place.gym.train': 'Тренироваться',
  'place.clinic': 'Поликлиника',
  'place.clinic.checkup': 'Пройти осмотр',
  'place.clinic.treat': 'Лечить хроническое',
  'place.home': 'Дом и семья',
  'place.home.child': 'Поговорить с ребёнком',
  'place.home.parents': 'Позаботиться о родителях',
  'place.home.partner': 'Ужин вдвоём',
  'place.bar': 'Бар и друзья',
  'place.bar.drink': 'Провести вечер',
  'place.temple': 'Храм и община',
  'place.temple.pray': 'Побыть в общине',
  'place.market': 'Рынок',
  'place.market.bargain': 'Поторговаться',
  'place.bank': 'Банк и биржа',
  'place.bank.stocks': 'Купить акции',
  'place.bank.crypto': 'Вложить в монету',
  'place.bank.borrow': 'Занять',
  'place.travel': 'Путешествие',
  'place.travel.go': 'Поехать',
  'place.migration': 'Отдел миграции',
  'place.migration.learn': 'Узнать условия переезда',
  'place.migration.apply': 'Подать документы',
  'place.rest': 'Отдых',
  'place.rest.rest': 'Отдохнуть',
  'place.casino': 'Казино',
  'place.casino.roulette': 'Рулетка',
  'place.casino.slots': 'Слоты',
  'place.casino.blackjack': 'Блэкджек',
  'place.lottery': 'Лотерейный киоск',
  'place.lottery.ticket': 'Купить билет',
  'place.street': 'Улица',
  'place.street.dice': 'Кости',
  'place.bookmaker': 'Букмекер',
  'place.bookmaker.bet': 'Поставить на чемпионат',

  'place.expect.work.shift': 'Скорее всего: деньги ↑ (4 % годового дохода), здоровье ↓ 1. В первый раз за год — смена как мини-игра, можно заслужить повышение.',
  'place.expect.work.vacation': 'Скорее всего: счастье ↑ 3, деньги ↓ (2 % дохода).',
  'place.expect.jobcenter.find': 'Собеседование, если работа уже ищется; иначе при следующем найме место на уровень выше.',
  'place.expect.jobcenter.change': 'Собеседование на уровень выше, если решение о смене уже стоит; иначе бонус к уровню при следующей смене.',
  'place.expect.jobcenter.parttime': 'Скорее всего: деньги ↑ (5 % ВВП на душу), здоровье ↓ 1.',
  'place.expect.courses.study': 'Скорее всего: знания ↑ 2, деньги ↓ (1 % ВВП на душу; до 18 лет бесплатно там, где школа длинная).',
  'place.expect.gym.train': 'Скорее всего: здоровье ↑ 2, счастье ↑ 1.',
  'place.expect.clinic.checkup': 'Осмотр: здоровье ↑ 1, деньги ↓ по доле платной медицины в стране.',
  'place.expect.clinic.treat': 'Очередь в поликлинике, когда врач предложил лечение: цена и ожидание зависят от окна. Без такого предложения идти незачем.',
  'place.expect.home.child': 'Разговор с ребёнком: отношения ↑ или ↓ в зависимости от ответа.',
  'place.expect.home.parents': 'Скорее всего: отношения ↑ 2, счастье ↑ 1.',
  'place.expect.home.partner': 'Скорее всего: отношения ↑ 3.',
  'place.expect.bar.drink': 'Скорее всего: отношения ↑ 3, счастье ↑ 2. С вероятностью {p} % начинается привычка к алкоголю.',
  'place.expect.temple.pray': 'Скорее всего: счастье ↑ 2, отношения ↑ 1.',
  'place.expect.market.bargain': 'Торг: сэкономить можно, но продавец может и уйти.',
  'place.expect.bank.stocks': 'Не азарт: ожидание +5 % в год при разбросе 18 %.',
  'place.expect.bank.crypto': 'Ожидание +3 % в год при разбросе 80 %; каждый взгляд на курс тянет сыграть.',
  'place.expect.bank.borrow': 'Ломбард или микрозайм: через год вернуть больше, не вернёте — долг и коллекторы.',
  'place.expect.travel.go': 'Скорее всего: счастье ↑ 5, деньги ↓ (30 % ВВП на душу). Запись в журнале с направлением.',
  'place.expect.migration.learn': 'Покажет шанс успеха и цену переезда. Ничего не меняет.',
  'place.expect.migration.apply': 'В следующем году придёт решение о переезде. Нужно {need} $ сбережений (или спонсор), возраст до сорока. Подать можно раз в пять лет.',
  'place.expect.rest.rest': 'Счастье ↑ 1 за очко.',
  'place.expect.casino.roulette': 'В среднем теряете {loss} % ставки за круг.',
  'place.expect.casino.slots': 'В среднем теряете {loss} % ставки за прокрут.',
  'place.expect.casino.blackjack': 'В среднем теряете {loss} % ставки за руку при базовой стратегии.',
  'place.expect.lottery.ticket': 'В среднем теряете {loss} % цены билета.',
  'place.expect.street.dice': 'В среднем теряете {loss} % ставки за бросок. На улице бывает опасно.',
  'place.expect.bookmaker.bet': 'В среднем теряете {loss} % ставки. Итог — в конце года.',

  'place.reason.noEnergy': 'Нет сил на этот год',
  'place.reason.enough': 'Хватит на сегодня',
  'place.reason.age': 'Не по возрасту',
  'place.reason.illegal': 'Здесь это запрещено',
  'place.reason.disabled': 'Скрыто в учебном режиме',
  'place.reason.city': 'Только в городе',
  'place.reason.noMoney': 'Не хватает денег',
  'place.reason.noJob': 'Нет работы',
  'place.reason.hasJob': 'Работа уже есть',
  'place.reason.shortTenure': 'Стаж меньше двух лет',
  'place.reason.noStrength': 'Нет сил',
  'place.reason.noFamily': 'Некого навестить',
  'place.reason.tooSoon': 'Слишком рано, подождите',
  'place.reason.notYet': 'Ещё не время',
  'place.reason.noNeed': 'Сейчас не нужно',
  'place.reason.inSchool': 'Пока учится',
  'place.reason.dead': 'Жизнь окончена',

  'place.work.shift.done': 'Вышли в смену: немного денег, немного усталости.',
  'place.work.vacation.done': 'Взяли отпуск: несколько дней без будильника.',
  'place.jobcenter.found': 'Сходили на биржу труда: следующее предложение будет лучше.',
  'place.jobcenter.changed': 'Присмотрели новое место: при смене работы уровень выше.',
  'place.jobcenter.parttime.done': 'Подработали: деньги есть, спина ноет.',
  'place.courses.studied': 'Позанимались на курсах.',
  'place.gym.trained': 'Потренировались.',
  'place.clinic.checkup.done': 'Прошли осмотр в поликлинике.',
  'place.home.parents.done': 'Навестили родителей.',
  'place.home.partner.done': 'Ужин вдвоём, без телефонов.',
  'place.bar.evening': 'Вечер с друзьями.',
  'place.bar.alcohol': 'Вечер с друзьями затянулся — и не в первый раз. Появилась привычка к алкоголю.',
  'place.temple.visited': 'Побыли в общине.',
  'place.travel.went': 'Съездили в {country}.',
  'place.migration.learned': 'Узнали условия переезда: шанс {chance} %, цена {cost} $.',
  'place.migration.applied': 'Подали документы на переезд.',
  'place.rest.done': 'Просто отдохнули.',

  // Цели (core-loop-v2 §4, goals.ts): мечта, цель года, вехи.
  'dream.snow': 'Увидеть снег',
  'dream.home': 'Свой дом',
  'dream.university': 'Университет',
  'dream.family': 'Большая семья',
  'dream.abroad': 'Уехать в другую страну',
  'dream.business': 'Открыть своё дело',
  'dream.eighty': 'Дожить до восьмидесяти',
  'dream.children': 'Стать родителем',
  'dream.travel': 'Большое путешествие',
  'dream.peace': 'Спокойная старость',
  'dream.chosen': 'Мечта на всю жизнь: {dream}.',
  'dream.achieved': 'Мечта сбылась: {dream}.',
  'dream.none': 'Без мечты',
  'dream.progress': 'Мечта: {dream} — {percent} %',
  'dream.failed': 'Мечта не сбылась: {dream}.',
  'dream.achievedAt': 'Мечта сбылась: {dream}, {ageAt}.',

  'goal.payDebt': 'Цель года: погасить долг {amount} $',
  'goal.findJob': 'Цель года: найти работу',
  'goal.health': 'Цель года: здоровье выше {target}',
  'goal.happiness': 'Цель года: счастье выше {target}',
  'goal.relations': 'Цель года: отношения выше {target}',
  'goal.save': 'Цель года: накопить {amount} $',
  'goal.knowledge': 'Цель года: знания выше {target}',
  'goal.done': 'Цель года выполнена.',

  'milestone.firstJob': 'Первая работа.',
  'milestone.first1000': 'Первые 1000 $.',
  'milestone.ownHome': 'Своё жильё.',
  'milestone.marriage': 'Брак.',
  'milestone.firstChild': 'Первый ребёнок.',
  'milestone.retire': 'Пенсия.',
  'milestone.tenYearsNoSmoking': 'Десять лет без курения.',
  'milestone.dream': 'Мечта сбылась.',
};
